'use strict';

// external imports
import _ from 'lodash';

// shared imports
import metaModel from '~/shared/src/utilities/metaModel';
import LocalizableError from '~/shared/src/base/LocalizableError';
import StackError from '~/shared/src/base/StackError';
import Item from '~/shared/src/ci/Item';
import Reference from '~/shared/src/ci/Reference';
import {
    Action,
    actionProps,
    ConfigData,
    DataSelectionMode
} from '~/shared/src/utilities/action';
import {
    BusinessProcess,
    businessProcessDataProps
} from '~/shared/src/utilities/modules/businessProcess';
import {
    BusinessProcessStep,
    businessProcessStepDataProps
} from '~/shared/src/utilities/businessProcessStep';
import {
    Calendar,
    calendarDataProps
} from '~/shared/src/utilities/calendar';
import {
    CalendarEvent,
    calendarEventDataProps
} from '~/shared/src/utilities/calendarEvent';
import {
    Chart,
    chartDataProps
} from '~/shared/src/utilities/chart';
import {
    ChartDataSet,
    chartDataSetDataProps
} from '~/shared/src/utilities/chartDataSet';
import {
    ChartScale,
    chartScaleDataProps
} from '~/shared/src/utilities/chartScale';
import {
    Column,
    columnDataProps
} from '~/shared/src/utilities/column';
import {
    Condition,
    conditionProps
} from '~/shared/src/utilities/condition';
import {
    buildDataDescriptor,
    DataDescriptor,
    DataDescriptorBuildParams,
    DataObjectProperties,
    DataStructure,
    extractDataObjectForItem,
    extractDataObjectsFromDataStructure,
    getDataObjectByOrigId,
    getObjectDataDescriptor,
    ObjectDataDescriptor,
    StaticDataDescriptor
} from '~/shared/src/utilities/dataStructure';
import {
    EditorConfig,
    editorConfig
} from '~/shared/src/utilities/editorConfig';
import {
    FilterConfig,
    filterConfig
} from '~/shared/src/utilities/filterConfig';
import {
    Format,
    formatProps
} from '~/shared/src/utilities/format';
import {
    File,
    fileProps
} from '~/shared/src/utilities/file';
import {
    Function,
    functionProps
} from '~/shared/src/utilities/function';
import {
    Graph,
    graphDataProps
} from '~/shared/src/utilities/graph';
import {
    BackendAction,
    backendActionProps,
    requiredBackendActionProps
} from '~/shared/src/utilities/modules/backendAction';
import {
    LandingPageData,
    landingPageDataProps,
    requiredLandingPageDataProps,
    LandingPage
} from '~/shared/src/utilities/landingPage';
import {
    MailAttachment,
    mailAttachmentMessages,
    mailAttachmentProps
} from '~/shared/src/utilities/mailAttachment';
import {
    MailServer,
    mailServerProps
} from '~/shared/src/utilities/mailServer';
import {
    Map,
    mapDataProps
} from '~/shared/src/utilities/map';
import {
    MapDataSet,
    mapDataSetDataProps
} from '~/shared/src/utilities/mapDataSet';
import {
    Message,
    MessageObject,
    messageObjectProps
} from '~/shared/src/utilities/message';
import {
    Overview,
    overviewDataProps
} from '~/shared/src/utilities/overview';
import {
    PanelConfig,
    panelConfigProps
} from '~/shared/src/utilities/panelConfig';
import {
    Query,
    queryProps
} from '~/shared/src/utilities/query';
import {
    Style,
    styleProps
} from '~/shared/src/utilities/style';
import {
    Validator,
    validatorProps
} from '~/shared/src/utilities/validator';
import {getIconCssClassName} from '~/shared/src/utilities/getCssClassName';
import ciHelper from '~/shared/src/utilities/dataUtils';
import {getDataStructureForSqlQuery} from '~/shared/src/utilities/modules/queryLoader';

// frontend imports
import {PanelPropsWithPanelType} from '~/frontend/src/actions/panelManager/openPanel';
import {PanelType} from '~/frontend/src/components/base/page/PageTabPanel/definitions';

// backend imports
///////////////////////////
////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////
////////
////////////////////////////////////////
//////////////
/////////////////////////////////////////////
////////
////////////////////////
///////////////////////
/////////////////////////////////////////////
//////////


export type ExtractedProperties = {
    id : number;
    key : string;
    name : string;
    [ propertyTypeName : string ] : ( any | {
        [ key : string ] : any;
    } )
};

export type ExtractedReferenceProperties = {
    id : number;
    srcItemId : number;
    destItemId : number;

    [ propertyTypeName : string ] : ( AttributeValue | {
        [ key : string ] : AttributeValue;
    } )
};

export type ImageData = {
    data : ExtractedProperties;
};

export type Images = {
    [ name : string ] : string;
};

export type Messages = {
    [ name : string ] : Message;
};

export type Styles = {
    [ name : string ] : Style[];
};


function extractDataStructure( item : Item, dataContext : string, withDeleted? : boolean, multipleRoots? : false ) : DataStructure | undefined
// @ts-expect-error multipleRoots is not a required parameter so it can follow an optional parameter.
function extractDataStructure( item : Item, dataContext : string, withDeleted? : boolean, multipleRoots : true ) : DataStructure[] | undefined
function extractDataStructure( item : Item, dataContext : string, withDeleted : boolean = false, multipleRoots = false ) : DataStructure | DataStructure[] | undefined {
    let retVal : DataStructure | DataStructure[] | undefined;

    // get root dataObject
    for ( const referenceId of item.references ) {
        const reference = global.data[ dataContext ]?.references?.[ referenceId ];

        if (
            reference
            && ( withDeleted || !reference.isDeleted() )
            && reference.destItemId === item.id
            && reference.srcItemId
            && reference.referenceSet.referenceType.name === 'isRootOf'
        ) {
            const srcItem = global.data[ dataContext ]?.items[ reference.srcItemId ];

            if (
                srcItem
                && ( withDeleted || !srcItem.isDeleted() )
                && srcItem.itemType.name === 'DataObject'
            ) {
                if ( multipleRoots ) {
                    if ( !retVal ) {
                        retVal = [];
                    }

                    ( retVal as DataStructure[] ).push( dataExtraction.extractDataStructureData( srcItem, dataContext, undefined, undefined, undefined, undefined, withDeleted ) );
                }
                else {
                    retVal = dataExtraction.extractDataStructureData( srcItem, dataContext, undefined, undefined, undefined, undefined, withDeleted );
                }
            }

            if ( multipleRoots ) {
                break;
            }
        }
    }

    return retVal;
}


export const dataExtraction = {

    /**
     * Extracts the action for a given item and dataContext
     *
     * @param {Item} item
     * @param {string} dataContext
     * @param {DataStructure} [dataStructure]
     * @param {string} [allowedAction='getsDataFor']
     *
     * @return {Action | undefined}
     */
    extractAction( item : Item, dataContext : string, dataStructure? : DataStructure, allowedAction : string = 'getsDataFor' ) : Action | undefined {
        let retVal : Action | undefined;

        if ( !dataStructure ) {
            dataStructure = dataExtraction.extractDataStructure( item, dataContext );
        }

        for ( const referenceId of item.references ) {
            const reference = global.data[ dataContext ]?.references?.[ referenceId ];

            if (
                reference
                && !reference.isDeleted()
                && reference.destItemId === item.id
                && reference.srcItemId
                && reference.referenceSet.referenceType.name === allowedAction
            ) {
                const srcItem = global.data[ dataContext ]?.items[ reference.srcItemId ];

                if (
                    srcItem
                    && !srcItem.isDeleted()
                    && srcItem.itemType.name === 'Action'
                ) {
                    retVal = dataExtraction.extractActionData( srcItem, dataContext, dataStructure );
                    break;
                }
            }
        }

        return retVal;
    },

    /**
     * Extracts action for a given item and dataContext
     *
     * @param {Item} item
     * @param {string} dataContext
     * @param {DataStructure} [dataStructure]
     *
     * @return {Action[]}
     */
    extractActions( item : Item, dataContext : string, dataStructure? : DataStructure ) : Action[] {
        const retVal : Action[] = [];

        if ( !dataStructure ) {
            dataStructure = dataExtraction.extractDataStructure( item, dataContext );
        }

        for ( const referenceId of item.references ) {
            const reference = global.data[ dataContext ]?.references?.[ referenceId ];

            if (
                reference
                && !reference.isDeleted()
                && reference.srcItemId === item.id
                && reference.destItemId
                && reference.referenceSet.referenceType.name === 'isTriggerFor'
            ) {
                const destItem = global.data[ dataContext ]?.items[ reference.destItemId ];

                if (
                    destItem
                    && !destItem.isDeleted()
                    && destItem.itemType.name === 'Action'
                ) {
                    retVal.push( dataExtraction.extractActionData( destItem, dataContext, dataStructure ) );

                    const referenceProperties : ConfigData = dataExtraction.extractPropertiesOfReference( reference );

                    delete referenceProperties.id;
                    delete referenceProperties.srcItemId;
                    delete referenceProperties.destItemId;

                    if ( Object.keys( referenceProperties ).length ) {
                        retVal[ retVal.length - 1 ].configs = {
                            ...retVal[ retVal.length - 1 ].configs,
                            [ '::referenceProperties' ]: referenceProperties
                        };
                    }
                }
            }
        }

        retVal.sort( ( action1, action2 ) => {
            let sortRetVal = 0;

            if ( action1.order && action2.order ) {
                if ( action1.order > action2.order ) {
                    sortRetVal = 1;
                }
                else if ( action1.order < action2.order ) {
                    sortRetVal = -1;
                }
            }

            return sortRetVal;
        } );

        return retVal;
    },

    /**
     * Extracts action for a given item and dataContext
     *
     * @param {Item} item
     * @param {string} dataContext
     *
     * @return {Action[]}
     */
    extractBackendActions( item : Item, dataContext : string ) : BackendAction[] {
        const retVal : BackendAction[] = [];

        for ( const referenceId of item.references ) {
            const reference = global.data[ dataContext ]?.references?.[ referenceId ];

            if (
                reference
                && !reference.isDeleted()
                && reference.srcItemId === item.id
                && reference.destItemId
                && reference.referenceSet.referenceType.name === 'isExecuting'
            ) {
                const destItem = global.data[ dataContext ]?.items[ reference.destItemId ];

                if (
                    destItem
                    && !destItem.isDeleted()
                    && destItem.itemType.name === 'BackendAction'
                ) {
                    const backendActionData = dataExtraction.extractBackendActionData( destItem, dataContext );

                    if ( backendActionData ) {
                        retVal.push( backendActionData );
                    }
                }
            }
        }

        retVal.sort( ( action1, action2 ) => {
            let sortRetVal = 0;

            if ( action1.order && action2.order ) {
                if ( action1.order > action2.order ) {
                    sortRetVal = 1;
                }
                else if ( action1.order < action2.order ) {
                    sortRetVal = -1;
                }
            }

            return sortRetVal;
        } );

        return retVal;
    },

    extractBusinessProcessSteps( item : Item, dataContext : string, dataStructure? : DataStructure ) : ( BusinessProcessStep | BusinessProcessStep[] )[] {
        const retVal : ( BusinessProcessStep | BusinessProcessStep[] )[] = [];

        if ( !dataStructure ) {
            dataStructure = dataExtraction.extractDataStructure( item, dataContext );
        }

        for ( const referenceId of item.references ) {
            const reference = global.data[ dataContext ]?.references?.[ referenceId ];

            if (
                reference
                && !reference.isDeleted()
                && reference.destItemId === item.id
                && reference.srcItemId
                && reference.referenceSet.referenceType.name === 'belongsTo'
            ) {
                const srcItem = global.data[ dataContext ]?.items[ reference.srcItemId ];

                if (
                    srcItem
                    && !srcItem.isDeleted()
                    && srcItem.itemType.name === 'BusinessProcessStep'
                ) {
                    const step = dataExtraction.extractBusinessProcessStepData( srcItem, dataContext, dataStructure );

                    if ( step ) {
                        let index = retVal.findIndex( s => Array.isArray( s ) && s[ 0 ].data.order === step.data.order );

                        if ( index !== -1 ) {
                            ( retVal[ index ] as BusinessProcessStep[] ).push( step );
                        }
                        else {
                            index = retVal.findIndex( s => !Array.isArray( s ) && s.data.order === step.data.order );

                            if ( index !== -1 ) {
                                retVal[ index ] = [ retVal[ index ] as BusinessProcessStep, step ];
                            }
                            else {
                                retVal.push( step );
                            }
                        }
                    }
                }
            }
        }

        retVal.sort( ( step1, step2 ) => {
            const step1Order = Array.isArray( step1 ) ? step1[ 0 ].data.order : step1.data.order,
                  step2Order = Array.isArray( step2 ) ? step2[ 0 ].data.order : step2.data.order;

            let sortRetVal = 0;

            if ( step1Order > step2Order ) {
                sortRetVal = 1;
            }
            else if ( step1Order < step2Order ) {
                sortRetVal = -1;
            }

            return sortRetVal;
        } );

        for ( const step of retVal ) {
            if ( Array.isArray( step ) ) {
                step.sort( ( step1, step2 ) => {
                    let sortRetVal = 0;

                    if ( step1.data.subOrder > step2.data.subOrder ) {
                        sortRetVal = 1;
                    }
                    else if ( step1.data.subOrder < step2.data.subOrder ) {
                        sortRetVal = -1;
                    }

                    return sortRetVal;
                } );
            }
        }

        return retVal;
    },

    /**
     * Extracts the calendar for a given item and dataContext
     *
     * @param {Item} item
     * @param {string} dataContext
     * @param {DataStructure} [dataStructure]
     *
     * @return {Calendar | undefined}
     */
    extractCalendar( item : Item, dataContext : string ) : Calendar | undefined {
        let retVal : Calendar | undefined;

        for ( const referenceId of item.references ) {
            const reference = global.data[ dataContext ]?.references?.[ referenceId ];

            if (
                reference
                && !reference.isDeleted()
                && reference.destItemId === item.id
                && reference.srcItemId
                && reference.referenceSet.referenceType.name === 'belongsTo'
            ) {
                const srcItem = global.data[ dataContext ]?.items[ reference.srcItemId ];

                if (
                    srcItem
                    && !srcItem.isDeleted()
                    && srcItem.itemType.name === 'Calendar'
                ) {
                    retVal = dataExtraction.extractCalendarData( srcItem, dataContext );
                    break;
                }
            }
        }

        return retVal;
    },

    /**
     * Extracts the calendars for a given item and dataContext
     *
     * @param {Item} item
     * @param {string} dataContext
     * @param {DataStructure} [dataStructure]
     *
     * @return {Item[]}
     */
    extractCalendarCalendars( item : Item, dataContext : string, dataStructure? : DataStructure ) : Calendar['data'][] {
        const retVal : Calendar['data'][] = [];

        if ( !dataStructure ) {
            dataStructure = dataExtraction.extractDataStructure( item, dataContext );
        }

        if ( dataStructure ) {
            for ( const referenceId of item.references ) {
                const reference = global.data[ dataContext ]?.references?.[ referenceId ];

                if (
                    reference
                    && !reference.isDeleted()
                    && reference.destItemId === item.id
                    && reference.srcItemId
                    && reference.referenceSet.referenceType.name === 'belongsTo'
                ) {
                    const srcItem = global.data[ dataContext ]?.items[ reference.srcItemId ];

                    if (
                        srcItem
                        && !srcItem.isDeleted()
                        && srcItem.itemType.name === 'Calendar'
                    ) {
                        const calendar      = dataExtraction.extractCalendarData( srcItem, dataContext ),
                              referenceData = _.pick(
                                  {
                                      ...dataExtraction.getDefaultsForReferenceSet( reference.schema, reference.referenceSetId ),
                                      ...dataExtraction.extractPropertiesOfReference( reference )
                                  },
                                  calendarDataProps.filter( prop => prop !== 'id' && prop !== 'key' && prop !== 'name' )
                              ),
                              calendarData  = {
                                  ...calendar.data,
                                  ...referenceData
                              };

                        retVal.push( calendarData );
                    }
                }
            }
        }

        return retVal;
    },

    /**
     * Extracts the calendar events for a given item and dataContext
     *
     * @param {Item} item
     * @param {string} dataContext
     * @param {DataStructure} [dataStructure]
     * @param {Query} [query]
     *
     * @return {Promise<CalendarEvent[]>}
     */
    async extractCalendarEvents( item : Item, dataContext : string, dataStructure? : DataStructure, query? : Query ) : Promise<CalendarEvent[]> {
        const retVal : CalendarEvent[] = [];

        if ( !dataStructure ) {
            dataStructure = dataExtraction.extractDataStructure( item, dataContext );
        }

        if ( dataStructure ) {
            for ( const referenceId of item.references ) {
                const reference = global.data[ dataContext ]?.references?.[ referenceId ];

                if (
                    reference
                    && !reference.isDeleted()
                    && reference.destItemId === item.id
                    && reference.srcItemId
                    && reference.referenceSet.referenceType.name === 'belongsTo'
                ) {
                    const srcItem = global.data[ dataContext ]?.items[ reference.srcItemId ];

                    if (
                        srcItem
                        && !srcItem.isDeleted()
                        && srcItem.itemType.name === 'CalendarEvent'
                    ) {
                        const calendarEvent = await dataExtraction.extractCalendarEventData( srcItem, dataContext, dataStructure, query );

                        if ( calendarEvent ) {
                            retVal.push( calendarEvent );
                        }
                    }
                }
            }

            retVal.sort( ( event1, event2 ) => {
                let sortRetVal = 0;

                if ( event1.data.order && event2.data.order ) {
                    if ( event1.data.order > event2.data.order ) {
                        sortRetVal = 1;
                    }
                    else if ( event1.data.order < event2.data.order ) {
                        sortRetVal = -1;
                    }
                }

                return sortRetVal;
            } );
        }

        return retVal;
    },

    /**
     * Extracts the chart for a given item and dataContext
     *
     * @param {Item} item
     * @param {string} dataContext
     *
     * @return {Chart | undefined}
     */
    extractChart( item : Item, dataContext : string ) : Chart | undefined {
        let retVal : Chart | undefined;

        for ( const referenceId of item.references ) {
            const reference = global.data[ dataContext ]?.references?.[ referenceId ];

            if (
                reference
                && !reference.isDeleted()
                && reference.destItemId === item.id
                && reference.srcItemId
                && reference.referenceSet.referenceType.name === 'belongsTo'
            ) {
                const srcItem = global.data[ dataContext ]?.items[ reference.srcItemId ];

                if (
                    srcItem
                    && !srcItem.isDeleted()
                    && srcItem.itemType.name === 'Chart'
                ) {
                    retVal = dataExtraction.extractChartData( srcItem, dataContext );

                    break;
                }
            }
        }

        return retVal;
    },

    /**
     * Extracts the chart data sets for a given item and dataContext
     *
     * @param {Item} item
     * @param {string} dataContext
     * @param {DataStructure} [dataStructure]
     *
     * @return {ChartDataSet[]}
     */
    extractChartDataSets( item : Item, dataContext : string, dataStructure : DataStructure ) : ChartDataSet[] {
        const retVal : ChartDataSet[] = [];

        for ( const referenceId of item.references ) {
            const reference = global.data[ dataContext ]?.references?.[ referenceId ];

            if (
                reference
                && !reference.isDeleted()
                && reference.destItemId === item.id
                && reference.srcItemId
                && reference.referenceSet.referenceType.name === 'belongsTo'
            ) {
                const srcItem = global.data[ dataContext ]?.items[ reference.srcItemId ];

                if (
                    srcItem
                    && !srcItem.isDeleted()
                    && srcItem.itemType.name === 'ChartDataSet'
                ) {
                    const dataSet = dataExtraction.extractChartDataSetData( srcItem, dataContext, dataStructure );

                    if ( dataSet ) {
                        retVal.push( dataSet );
                    }
                }
            }
        }

        retVal.sort( ( event1, event2 ) => {
            let sortRetVal = 0;

            if ( event1.data.order && event2.data.order ) {
                if ( event1.data.order > event2.data.order ) {
                    sortRetVal = 1;
                }
                else if ( event1.data.order < event2.data.order ) {
                    sortRetVal = -1;
                }
            }

            return sortRetVal;
        } );

        return retVal;
    },

    /**
     * Extracts the chart scales for a given item and dataContext
     *
     * @param {Item} item
     * @param {string} dataContext
     * @param {DataStructure} [dataStructure]
     *
     * @return {ChartScale[]}
     */
    extractChartScales( item : Item, dataContext : string ) : ChartScale[] {
        const retVal : ChartScale[] = [];

        for ( const referenceId of item.references ) {
            const reference = global.data[ dataContext ]?.references?.[ referenceId ];

            if (
                reference
                && !reference.isDeleted()
                && reference.destItemId === item.id
                && reference.srcItemId
                && reference.referenceSet.referenceType.name === 'belongsTo'
            ) {
                const srcItem = global.data[ dataContext ]?.items[ reference.srcItemId ];

                if (
                    srcItem
                    && !srcItem.isDeleted()
                    && srcItem.itemType.name === 'ChartScale'
                ) {
                    const dataSet = dataExtraction.extractChartScaleData( srcItem, dataContext );

                    if ( dataSet ) {
                        retVal.push( dataSet );
                    }
                }
            }
        }

        return retVal;
    },

    /**
     * Recursively extracts the data structure and the columns configuration from the loaded overview config (via global.data[ dataContext ]).
     *
     * @param {Item} item
     * @param {string} dataContext
     * @param {DataStructure} dataStructure
     *
     * @return {Column[]}
     */
    extractColumns( item : Item, dataContext : string, dataStructure : DataStructure ) : Column[] {
        let retVal : Column[] = [];

        const dataObjectIsReferencedToDataObjectReferenceSetId = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( 'DataObject', 'DataObject', 'isReferencedTo', 'src' )?.[ 0 ]?.id,
              dataObjectProvidesDataForColumnReferenceSetId    = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( 'DataObject', 'Column', 'providesDataFor', 'src' )?.[ 0 ]?.id,
              columnBelongsToOverviewReferenceSetId            = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( 'Column', 'Overview', 'belongsTo', 'src' )?.[ 0 ]?.id;

        for ( const referenceId of item.references ) {
            const reference = global.data[ dataContext ]?.references?.[ referenceId ];

            if (
                reference
                && !reference.isDeleted()
            ) {
                const srcItem  = global.data[ dataContext ]?.items[ reference.srcItemId ],
                      destItem = global.data[ dataContext ]?.items[ reference.destItemId ];

                // look down for item (DataObject) <- isReferencedTo - DataObject
                if (
                    reference.referenceSetId === dataObjectIsReferencedToDataObjectReferenceSetId
                    && reference.destItemId === item.id
                    && srcItem
                    && !srcItem.isDeleted()
                ) {
                    retVal = retVal.concat( this.extractColumns( srcItem, dataContext, dataStructure ) );
                }

                // look up for item (DataObject) - providesDataFor -> Column
                else if (
                    reference.referenceSetId === dataObjectProvidesDataForColumnReferenceSetId
                    && reference.srcItemId === item.id
                    && destItem
                    && !destItem.isDeleted()
                ) {
                    retVal.push( this.extractColumnData( destItem, dataContext, dataStructure ) );
                }

                // look down for item( Overview ) <- belongsTo - Column
                else if (
                    reference.referenceSetId === columnBelongsToOverviewReferenceSetId
                    && reference.destItemId === item.id
                    && srcItem
                    && !srcItem.isDeleted()
                ) {
                    retVal.push( this.extractColumnData( srcItem, dataContext, dataStructure ) );
                }
            }
        }

        // order columns
        retVal.sort( ( column1, column2 ) => {
            const order1 = column1.data.order ?? Number.POSITIVE_INFINITY,
                  order2 = column2.data.order ?? Number.POSITIVE_INFINITY;

            let sortRetVal = 0;

            if ( order1 > order2 ) {
                sortRetVal = 1;
            }
            else if ( order1 < order2 ) {
                sortRetVal = -1;
            }

            return sortRetVal;
        } );

        return retVal;
    },

    /**
     * Extracts data for style from given item
     *
     * @param {Item} item
     * @param {string} dataContext
     * @param {DataStructure} [dataStructure]
     * @param {DataDescriptor} [defaultDataDescriptor]
     * @param {boolean} [ignoreErrors=false]
     *
     * @return {Condition | undefined}
     */
    extractCondition( item : Item, dataContext : string, dataStructure? : DataStructure, defaultDataDescriptor? : DataDescriptor, ignoreErrors : boolean = false ) : Condition | undefined {
        let retVal : Condition | undefined;

        if ( !dataStructure ) {
            dataStructure = dataExtraction.extractDataStructure( item, dataContext );
        }

        for ( const referenceId of item.references ) {
            const reference = global.data[ dataContext ]?.references?.[ referenceId ];

            if (
                reference
                && !reference.isDeleted()
            ) {
                const srcItem  = global.data[ dataContext ]?.items[ reference.srcItemId ],
                      destItem = global.data[ dataContext ]?.items[ reference.destItemId ];

                if (
                    srcItem
                    && !srcItem.isDeleted()
                    && srcItem.itemType.name === 'Condition'
                ) {
                    retVal = dataExtraction.extractConditionData( srcItem, dataContext, dataStructure, defaultDataDescriptor, undefined, ignoreErrors );

                    break;
                }
                else if (
                    destItem
                    && !destItem.isDeleted()
                    && destItem.itemType.name === 'Condition'
                ) {
                    retVal = dataExtraction.extractConditionData( destItem, dataContext, dataStructure, defaultDataDescriptor, undefined, ignoreErrors );

                    break;
                }
            }
        }

        return retVal;
    },

    /**
     * Extracts the dataStructure for given item and dataContext
     *
     * @param {Item} item
     * @param {string} dataContext
     * @param {boolean} [withDeleted=false] return deleted items too
     *
     * @return {DataStructure | undefined}
     */
    extractDataStructure,

    /**
     * Extracts format for a given item and dataContext
     *
     * @param {Item} item
     * @param {string} dataContext
     *
     * @return {Format | undefined}
     */
    extractFormat( item : Item, dataContext : string ) : Format | undefined {
        let retVal : Format | undefined;

        for ( const referenceId of item.references ) {
            const reference = global.data[ dataContext ]?.references?.[ referenceId ];

            if (
                reference
                && !reference.isDeleted()
                && reference.destItemId === item.id
                && reference.srcItemId
            ) {
                const srcItem = global.data[ dataContext ]?.items[ reference.srcItemId ];

                if (
                    srcItem
                    && !srcItem.isDeleted()
                    && srcItem.itemType.name === 'Format'
                ) {
                    const data = {
                        ...dataExtraction.getDefaultsForItemType( srcItem.schema, srcItem.itemType.name ),
                        ...dataExtraction.extractPropertiesOfItem( srcItem )
                    };

                    if ( data.options && typeof data.options === 'string' ) {
                        try {
                            data.options = JSON.parse( data.options );
                        }
                        catch ( e ) {
                            const stackError = new StackError(
                                'YN.base.backend.dataExtraction.extractFormat.optionsNoValidJSON',
                                {
                                    key: data.key
                                }
                            );

                            if ( e instanceof Error || typeof e === 'string' ) {
                                stackError.add( e );
                            }

                            throw ( stackError );
                        }
                    }

                    retVal = _.pick( data, formatProps ) as Format;
                }
            }
        }

        return retVal;
    },

    /**
     * Extracts format for a given item and dataContext
     *
     * @param {Item} item
     * @param {string} dataContext
     * @param {DataStructure} [dataStructure]
     *
     * @return {Function[]}
     */
    extractFunctions( item : Item, dataContext : string, dataStructure? : DataStructure ) : Function[] {
        const retVal : Function[] = [];

        if ( !dataStructure ) {
            dataStructure = dataExtraction.extractDataStructure( item, dataContext );
        }

        for ( const referenceId of item.references ) {
            const reference = global.data[ dataContext ]?.references?.[ referenceId ];

            if (
                reference
                && reference.destItemId === item.id
                && reference.srcItemId
                && ( reference.referenceSet.referenceType.name === 'belongsTo' || reference.referenceSet.referenceType.name === 'isManipulatorFor' )
            ) {
                const srcItem = global.data[ dataContext ]?.items[ reference.srcItemId ];

                if (
                    srcItem
                    && !srcItem.isDeleted()
                    && srcItem.itemType.name === 'Function'
                ) {
                    retVal.push( dataExtraction.extractFunctionData( srcItem, dataContext, dataStructure ) );
                }
            }
        }

        retVal.sort( ( function1, function2 ) => {
            let sortRetVal = 0;

            if ( function1.order && function2.order ) {
                if ( function1.order > function2.order ) {
                    sortRetVal = 1;
                }
                else if ( function1.order < function2.order ) {
                    sortRetVal = -1;
                }
            }

            return sortRetVal;
        } );

        return retVal;
    },

    /**
     * Extracts the calendar for a given item and dataContext
     *
     * @param {Item} item
     * @param {string} dataContext
     * @param {DataStructure} [dataStructure]
     *
     * @return {Calendar | undefined}
     */
    extractGraph( item : Item, dataContext : string ) : Graph | undefined {
        let retVal : Graph | undefined;

        for ( const referenceId of item.references ) {
            const reference = global.data[ dataContext ]?.references?.[ referenceId ];

            if (
                reference
                && !reference.isDeleted()
                && reference.destItemId === item.id
                && reference.srcItemId
                && reference.referenceSet.referenceType.name === 'belongsTo'
            ) {
                const srcItem = global.data[ dataContext ]?.items[ reference.srcItemId ];

                if (
                    srcItem
                    && !srcItem.isDeleted()
                    && srcItem.itemType.name === 'Graph'
                ) {
                    retVal = dataExtraction.extractGraphData( srcItem, dataContext );
                    break;
                }
            }
        }

        return retVal;
    },

    /**
     * Extracts images for a given item and dataContext
     *
     * @param {Item} item
     * @param {string} dataContext
     * @param {string[]} allowedImages
     *
     * @return {Images}
     */
    extractImages( item : Item, dataContext : string, allowedImages? : string[] ) : Images {
        const retVal : Images = {};

        for ( const referenceId of item.references ) {
            const reference = global.data[ dataContext ]?.references?.[ referenceId ];

            if (
                reference
                && !reference.isDeleted()
                && reference.destItemId === item.id
                && reference.srcItemId
            ) {
                const srcItem                         = global.data[ dataContext ]?.items[ reference.srcItemId ],
                      referenceTypeName               = reference.referenceSet.referenceType.name,
                      extractedIsForReferenceTypeName = dataExtraction.extractIsForReferenceTypeName( referenceTypeName );

                if (
                    srcItem
                    && !srcItem.isDeleted()
                    && srcItem.itemType.name === 'Image'
                    && (
                        allowedImages === undefined
                        || allowedImages.includes( extractedIsForReferenceTypeName )
                    )
                ) {
                    const imageData = dataExtraction.extractPropertiesOfItem( srcItem );

                    if ( extractedIsForReferenceTypeName === 'icon' ) {
                        if ( imageData.cssClass ) {
                            retVal[ extractedIsForReferenceTypeName ] = imageData.cssClass;
                        }
                        else {
                            retVal[ extractedIsForReferenceTypeName ] = getIconCssClassName( 'byRef', srcItem.key, 'CFG' );
                        }
                    }
                    else {
                        retVal[ extractedIsForReferenceTypeName ] = '';

                        if ( imageData.cssClass ) {
                            retVal[ extractedIsForReferenceTypeName ] += `cssClass( ${imageData.cssClass} )`;
                        }

                        if ( imageData.content ) {
                            for ( const key of Object.keys( imageData.content ) as string[] ) {
                                if ( retVal[ extractedIsForReferenceTypeName ] ) {
                                    retVal[ extractedIsForReferenceTypeName ] += '::rn::'
                                }

                                retVal[ extractedIsForReferenceTypeName ] += `imageData( "key": "${key}", "value": "${imageData.content[ key ]}" )`;
                            }
                        }
                    }
                }
            }
        }

        return retVal;
    },

    extractLandingPage( item : Item, dataContext : string ) : LandingPage | undefined {
        const landingPageData = this.extractLandingPageData( item );

        let retVal : LandingPage | undefined;

        if ( landingPageData ) {
            let panelConfig : number | undefined,
                user : string | undefined;

            for ( const referenceId of item.references ) {
                const reference = global.data[ dataContext ]?.references?.[ referenceId ];

                if (
                    reference
                    && !reference.isDeleted()
                    && reference.srcItemId === item.id
                    && reference.destItemId
                ) {
                    const destItem = global.data[ dataContext ]?.items[ reference.destItemId ];

                    if (
                        destItem
                        && !destItem.isDeleted()
                        && destItem.itemType.name === 'Application'
                    ) {
                        retVal = {
                            ...landingPageData,
                            application: destItem.key
                        };
                    }
                }
                else if (
                    reference
                    && !reference.isDeleted()
                    && reference.destItemId === item.id
                    && reference.srcItemId
                ) {
                    const srcItem = global.data[ dataContext ]?.items[ reference.srcItemId ];

                    if (
                        srcItem
                        && !srcItem.isDeleted()
                    ) {
                        if ( srcItem.itemType.name === 'PanelConfig' ) {
                            panelConfig = srcItem.id;
                        }
                        else if ( srcItem.itemType.name === 'User' ) {
                            user = srcItem.key;
                        }
                    }
                }
            }

            if ( retVal && panelConfig ) {
                retVal.panelConfig = panelConfig;
            }

            if ( retVal && user ) {
                retVal.user = user;
            }
        }

        return retVal;
    },

    /**
     * Extracts format for a given item and dataContext
     *
     * @param {Item} item
     * @param {string} dataContext
     * @param {DataStructure} [dataStructure]
     *
     * @return {MailAttachment | undefined}
     */
    extractMailAttachment( item : Item, dataContext : string, dataStructure? : DataStructure ) : MailAttachment | undefined {
        let retVal : MailAttachment | undefined;

        if ( !dataStructure ) {
            dataStructure = dataExtraction.extractDataStructure( item, dataContext );
        }

        for ( const referenceId of item.references ) {
            const reference = global.data[ dataContext ]?.references?.[ referenceId ];

            if (
                reference
                && !reference.isDeleted()
                && reference.destItemId === item.id
                && reference.srcItemId
            ) {
                const srcItem = global.data[ dataContext ]?.items[ reference.srcItemId ];

                if (
                    srcItem
                    && !srcItem.isDeleted()
                    && srcItem.itemType.name === 'MailAttachment'
                ) {
                    retVal = dataExtraction.extractMailAttachmentData( srcItem, dataContext, dataStructure );
                }
            }
        }

        return retVal;
    },

    /**
     * Extracts the map dataSets for a given item and dataContext
     *
     * @param {Item} item
     * @param {string} dataContext
     *
     * @return {MapDataSet[]}
     */
    extractMapDataSetsDataStructureQueryOnly( item : Item, dataContext : string ) : MapDataSet[] {
        const retVal : MapDataSet[] = [];

        for ( const referenceId of item.references ) {
            const reference = global.data[ dataContext ]?.references?.[ referenceId ];

            if (
                reference
                && !reference.isDeleted()
                && reference.destItemId === item.id
                && reference.srcItemId
                && reference.referenceSet.referenceType.name === 'getsDataFor'
            ) {
                const srcItem = global.data[ dataContext ]?.items[ reference.srcItemId ];

                if (
                    srcItem
                    && !srcItem.isDeleted()
                    && srcItem.itemType.name === 'MapDataSet'
                ) {
                    const mapDataSet = dataExtraction.extractMapDataSetDataDataStructureQueryOnly( srcItem, dataContext );

                    if ( mapDataSet ) {
                        retVal.push( mapDataSet );
                    }
                }
            }
        }

        retVal.sort( ( mapDataSet1, mapDataSet2 ) => {
            if ( mapDataSet1.data.order > mapDataSet2.data.order ) {
                return 1;
            }
            else if ( mapDataSet1.data.order < mapDataSet2.data.order ) {
                return -1;
            }
            else {
                return 0;
            }
        } );

        return retVal;
    },

    /**
     * Extracts mailServer for a given item and dataContext
     *
     * @param {Item} item
     * @param {string} dataContext
     *
     * @return {MailServer | undefined}
     */
    extractMailServer( item : Item, dataContext : string ) : MailServer | undefined {
        let retVal : MailServer | undefined;

        for ( const referenceId of item.references ) {
            const reference = global.data[ dataContext ]?.references?.[ referenceId ];

            if (
                reference
                && reference.destItemId === item.id
                && reference.srcItemId
            ) {
                const srcItem = global.data[ dataContext ]?.items[ reference.srcItemId ];

                if (
                    srcItem
                    && !srcItem.isDeleted()
                    && srcItem.itemType.name === 'MailServer'
                ) {
                    retVal = dataExtraction.extractMailServerData( srcItem );
                    break;
                }
            }
        }

        return retVal;
    },

    /**
     * Extracts the map dataSets for a given item and dataContext
     *
     * @param {Item} item
     * @param {string} dataContext
     *
     * @return {Promise<MapDataSet[]>}
     */
    async extractMapDataSets( item : Item, dataContext : string ) : Promise<MapDataSet[]> {
        const retVal : MapDataSet[] = [];

        for ( const referenceId of item.references ) {
            const reference = global.data[ dataContext ]?.references?.[ referenceId ];

            if (
                reference
                && !reference.isDeleted()
                && reference.destItemId === item.id
                && reference.srcItemId
                && reference.referenceSet.referenceType.name === 'getsDataFor'
            ) {
                const srcItem = global.data[ dataContext ]?.items[ reference.srcItemId ];

                if (
                    srcItem
                    && !srcItem.isDeleted()
                    && srcItem.itemType.name === 'MapDataSet'
                ) {
                    const mapDataSet = await dataExtraction.extractMapDataSetData( srcItem, dataContext );

                    if ( mapDataSet ) {
                        retVal.push( mapDataSet );
                    }
                }
            }
        }

        return retVal;
    },

    /**
     * Extracts messages for a given item and dataContext
     *
     * @param {Item} item
     * @param {string} dataContext
     * @param {string[]} [allowedMessages]
     * @param {DataStructure} [dataStructure]
     *
     * @return {Messages}
     */
    extractMessages( item : Item, dataContext : string, allowedMessages? : string[], dataStructure? : DataStructure ) : Messages {
        const retVal : Messages = {};

        if ( !dataStructure ) {
            dataStructure = dataExtraction.extractDataStructure( item, dataContext );
        }

        for ( const referenceId of item.references ) {
            const reference = global.data[ dataContext ]?.references?.[ referenceId ];

            if (
                reference
                && !reference.isDeleted()
                && reference.destItemId === item.id
                && reference.srcItemId
            ) {
                const srcItem                         = global.data[ dataContext ]?.items[ reference.srcItemId ],
                      referenceTypeName               = reference.referenceSet.referenceType.name,
                      extractedIsForReferenceTypeName = dataExtraction.extractIsForReferenceTypeName( referenceTypeName );

                if (
                    srcItem
                    && !srcItem.isDeleted()
                    && srcItem.itemType.name === 'Message'
                    && (
                        allowedMessages === undefined
                        || allowedMessages.includes( extractedIsForReferenceTypeName )
                    )
                ) {
                    if ( srcItem.id > 0 && !srcItem.isModified() ) {
                        retVal[ extractedIsForReferenceTypeName ] = dataExtraction.extractMessageData( srcItem, dataContext, dataStructure );
                    }
                    else {
                        const contentPropertyTypeId = metaModel[ srcItem.schema ].getPropertyType( 'Content' )?.id ?? 0,
                              contentEnum           = srcItem.enumProperties.get( contentPropertyTypeId );

                        if ( contentEnum?.properties.size ) {
                            retVal[ extractedIsForReferenceTypeName ] = '';
                            for ( const property of contentEnum.properties.values() ) {
                                retVal[ extractedIsForReferenceTypeName ] += `{${property.key}: ${property.value}}`;
                            }
                        }
                        else {
                            retVal[ extractedIsForReferenceTypeName ] = dataExtraction.extractMessageData( srcItem, dataContext, dataStructure );
                        }
                    }
                }
            }
        }

        return retVal;
    },

    extractPanelConfig( item : Item, dataContext : string ) : PanelPropsWithPanelType | undefined {
        let retVal : PanelPropsWithPanelType | undefined;

        const panelConfigData                          = this.extractPanelConfigData( item ),
              viewIsCalledByPanelConfigReferenceSetIds = metaModel[ 'CFG' ].getReferenceSetsForItemTypeAndReferenceType( 'PanelConfig', 'isCalledBy', 'dest' ).map( rSet => rSet.id );

        // get panelType and viewKey (if) by PanelConfig related View
        for ( const referenceSetId of viewIsCalledByPanelConfigReferenceSetIds ) {
            const viewReferences = ciHelper.findReferences( dataContext, referenceSetId, item.id );

            if ( viewReferences?.[ 0 ]?.srcItem && viewReferences?.[ 0 ]?.destItem ) {

                if ( viewReferences[ 0 ].srcItem.itemType.name !== 'BaseView' ) {
                    retVal = {
                        // @ts-expect-error this is the initialization of panelConfig with this PanelType
                        panelType: viewReferences[ 0 ].srcItem.itemType.name as PanelType,
                        viewKey  : viewReferences[ 0 ].srcItem.key,
                        viewName : viewReferences[ 0 ].srcItem.name
                    };
                }
                else {
                    retVal = {
                        // @ts-expect-error this is the initialization of panelConfig with this PanelType
                        panelType: viewReferences[ 0 ].srcItem.key as PanelType
                    };
                }

                break;
            }
        }

        if ( !!retVal || panelConfigData.config ) {
            if ( !retVal ) {
                retVal = {} as PanelPropsWithPanelType;
            }

            retVal.schema = panelConfigData.schema ?? 'DATA' as Schema;
            if (
                retVal.panelType !== 'BusinessProcessDesignerView'
                && retVal.panelType !== 'ChartDesignerView'
                && retVal.panelType !== 'FinderDetail'
                && retVal.panelType !== 'FinderOverview'
                && retVal.panelType !== 'Form'
                && retVal.panelType !== 'FormDesignerView'
                && retVal.panelType !== 'Overview'
                && retVal.panelType !== 'OverviewDesignerView'
                && retVal.panelType !== 'QueryExecute'
                && retVal.panelType !== 'TreeTest'
                && retVal.panelType !== 'XmlImport'
            ) {
                retVal.rootItemKey = panelConfigData.rootItemKey;

                if (
                    retVal.panelType !== 'GraphDesignerView'
                    && retVal.panelType !== 'MapSearchView'
                ) {
                    retVal.rootItemType = panelConfigData.rootItemType;
                }
            }
            retVal.panelManagerConfig = {
                isBlocking : panelConfigData.isBlocking,
                openAsPanel: panelConfigData.openAsPanel,
                openAsPopup: panelConfigData.openAsPopup,
                condition  : panelConfigData.condition
            };

            // override with config
            if ( panelConfigData.config ) {
                try {
                    retVal = {
                        ...retVal,
                        ...panelConfigData.config
                    };
                }
                catch ( e ) {
                    // silent
                }
            }
        }

        return retVal;
    },

    /**
     * Extracts the IDs of PanelConfigs for a given item and dataContext
     *
     * @param {Item} item
     * @param {string} dataContext
     *
     * @return {number[]}
     */
    extractPanelConfigIds( item : Item, dataContext : string ) : number[] {
        const retVal : number[] = [];

        for ( const referenceId of item.references ) {
            const reference = global.data[ dataContext ]?.references?.[ referenceId ];

            if (
                reference
                && !reference.isDeleted()
                && reference.destItemId === item.id
                && reference.srcItemId
            ) {
                const srcItem = global.data[ dataContext ]?.items[ reference.srcItemId ];

                if (
                    srcItem
                    && !srcItem.isDeleted()
                    && srcItem.itemType.name === 'PanelConfig'
                ) {
                    retVal.push( reference.srcItemId );
                }
            }
        }

        return retVal;
    },

    /**
     * Extracts the permissions for a given item and dataContext
     *
     * @param {Item} item
     * @param {string} dataContext
     *
     * @return {GroupPermissions}
     */
    extractPermissions( item : Item, dataContext : string ) : GroupPermissions {
        const retVal : GroupPermissions = {};

        for ( const referenceId of item.references ) {
            const reference = global.data[ dataContext ]?.references?.[ referenceId ];

            if (
                reference
                && !reference.isDeleted()
                && reference.destItemId === item.id
                && reference.srcItemId
            ) {
                const srcItem           = global.data[ dataContext ]?.items[ reference.srcItemId ],
                      referenceTypeName = reference.referenceSet.referenceType.name as GroupPermissionName; // FIXME: ist hier einfach ' as ' ok?

                if (
                    srcItem
                    && !srcItem.isDeleted()
                    && srcItem.itemType.name === 'Group'
                ) {
                    if ( !retVal[ referenceTypeName ] ) {
                        retVal[ referenceTypeName ] = [] as string[];
                    }
                    retVal[ referenceTypeName ]!.push( srcItem.key );
                }
            }
        }

        return retVal;
    },

    /**
     * Extracts the query for a given item and dataContext
     *
     * @param {Item} item
     * @param {string} dataContext
     * @param {string} [allowedQuery = 'getsDataFor']
     *
     * @return {Query | undefined}
     */
    extractQuery( item : Item, dataContext : string, allowedQuery : string = 'getsDataFor' ) : Query | undefined {
        let retVal : Query | undefined;

        for ( const referenceId of item.references ) {
            const reference = global.data[ dataContext ]?.references?.[ referenceId ];

            if (
                reference
                && !reference.isDeleted()
                && reference.destItemId === item.id
                && reference.srcItemId
                && reference.referenceSet.referenceType.name === allowedQuery
            ) {
                const srcItem = global.data[ dataContext ]?.items[ reference.srcItemId ];

                if (
                    srcItem
                    && !srcItem.isDeleted()
                    && srcItem.itemType.name === 'Query'
                ) {
                    retVal = dataExtraction.extractQueryData( srcItem, dataContext );
                    break;
                }
            }
        }

        return retVal;
    },

    /**
     * Extracts styles for a given item and dataContext in an ordered manner
     *
     * @param {Item} item
     * @param {string} dataContext
     * @param {DataStructure} dataStructure
     * @param {string[]} [allowedStyles]
     * @param {DataDescriptor} [defaultDataDescriptor]
     *
     * @return {Styles}
     */
    extractStyles( item : Item, dataContext : string, dataStructure : DataStructure, allowedStyles? : string[], defaultDataDescriptor? : DataDescriptor ) : Styles {
        const retVal : Styles = {};

        for ( const referenceId of item.references ) {
            const reference = global.data[ dataContext ]?.references?.[ referenceId ];

            if (
                reference
                && !reference.isDeleted()
                && reference.destItemId === item.id
                && reference.srcItemId
            ) {
                const srcItem                         = global.data[ dataContext ]?.items[ reference.srcItemId ],
                      referenceTypeName               = reference.referenceSet.referenceType.name,
                      extractedIsForReferenceTypeName = dataExtraction.extractIsForReferenceTypeName( referenceTypeName );

                if (
                    srcItem
                    && !srcItem.isDeleted()
                    && srcItem.itemType.name === 'Style'
                    && (
                        allowedStyles === undefined
                        || allowedStyles.includes( extractedIsForReferenceTypeName )
                    )
                ) {
                    if ( !retVal[ extractedIsForReferenceTypeName ] ) {
                        retVal[ extractedIsForReferenceTypeName ] = [] as Style[];
                    }

                    retVal[ extractedIsForReferenceTypeName ].push( dataExtraction.extractStyleData( srcItem, dataContext, dataStructure, defaultDataDescriptor ) );
                }
            }
        }

        // order styles
        for ( const name of Object.keys( retVal ) ) {
            retVal[ name ].sort( ( style1, style2 ) => {
                let sortRetVal = 0;

                if ( style1.order && style2.order ) {
                    if ( style1.order > style2.order ) {
                        sortRetVal = 1;
                    }
                    else if ( style1.order < style2.order ) {
                        sortRetVal = -1;
                    }
                }

                return sortRetVal;
            } );
        }

        return retVal;
    },

    /**
     * Extracts format for a given item and dataContext
     *
     * @param {Item} item
     * @param {string} dataContext
     * @param {DataStructure} [dataStructure]
     *
     * @return {Function[]}
     */
    extractValidators( item : Item, dataContext : string, dataStructure? : DataStructure ) : Validator[] {
        const retVal : Validator[] = [];

        if ( !dataStructure ) {
            dataStructure = dataExtraction.extractDataStructure( item, dataContext );
        }

        for ( const referenceId of item.references ) {
            const reference = global.data[ dataContext ]?.references?.[ referenceId ];

            if (
                reference
                && !reference.isDeleted()
                && reference.destItemId === item.id
                && reference.srcItemId
                && reference.referenceSet.referenceType.name === 'isValidatorFor'
            ) {
                const srcItem = global.data[ dataContext ]?.items[ reference.srcItemId ];

                if (
                    srcItem
                    && !srcItem.isDeleted()
                    && srcItem.itemType.name === 'Validator'
                ) {
                    retVal.push( dataExtraction.extractValidatorData( srcItem, dataContext, dataStructure ) );
                }
            }
        }

        retVal.sort( ( function1, function2 ) => {
            let sortRetVal = 0;

            if ( function1.order && function2.order ) {
                if ( function1.order > function2.order ) {
                    sortRetVal = 1;
                }
                else if ( function1.order < function2.order ) {
                    sortRetVal = -1;
                }
            }

            return sortRetVal;
        } );

        return retVal;
    },

    /**
     * Extracts data for action from given item
     *
     * @param {Item} item
     * @param {string} dataContext
     * @param {DataStructure} [dataStructure]
     *
     * @return {Action}
     */
    extractActionData( item : Item, dataContext : string, dataStructure? : DataStructure ) : Action {
        const data = {
            ...dataExtraction.getDefaultsForItemType( item.schema, item.itemType.name ),
            ...dataExtraction.extractPropertiesOfItem( item ),
            dataObjects: {} as Action[ 'dataObjects' ],
            configs    : {} as Action[ 'configs' ]
        };

        if ( dataStructure ) {
            for ( const referenceId of item.references ) {
                const reference = global.data[ dataContext ]?.references?.[ referenceId ];

                if (
                    reference
                    && !reference.isDeleted()
                    && reference.destItemId === item.id
                    && reference.srcItemId
                    && reference.referenceSet.referenceType.name === 'isUsedBy'
                ) {
                    const srcItem                              = global.data[ dataContext ]?.items[ reference.srcItemId ],
                          referenceData                        = dataExtraction.extractPropertiesOfReference( reference ),
                          dataObjectIsUsedByActionReferenceSet = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( 'DataObject', 'Action', 'isUsedBy', 'src' )?.[ 0 ],
                          dataObjectProperties                 = getDataObjectByOrigId( reference.srcItemId, dataStructure ),
                          configProperties                     = this.extractPropertiesOfItem( srcItem ),
                          label                                = ( referenceData.label ?? String( srcItem?.key ) ) as string,
                          dataSelectionMode                    = ( referenceData.dataSelectionMode ?? metaModel[ 'CFG' ].getReferencePropertySetForReferenceSetAndPropertyType( dataObjectIsUsedByActionReferenceSet.id, 'DataSelectionMode' )?.valueDefault ?? 'single' ) as DataSelectionMode;

                    if (
                        srcItem
                        && !srcItem.isDeleted()
                        && srcItem.itemType.name === 'DataObject'
                        && dataObjectProperties
                    ) {
                        data.dataObjects[ label ] = {
                            ...dataObjectProperties,
                            dataSelectionMode
                        };
                    }
                    else if (
                        srcItem
                        && !srcItem.isDeleted()
                        && srcItem.itemType.name === 'Config'
                        && configProperties
                    ) {
                        data.configs![ label ] = {
                            ...configProperties
                        };
                    }
                }
            }
        }

        return _.pick( data, actionProps ) as Action;
    },

    /**
     * Extracts data for backendAction from given item
     *
     * @param {Item} item
     * @param {string} dataContext
     *
     * @return {Function}
     */
    extractBackendActionData( item : Item, dataContext : string ) : BackendAction | undefined {
        const props = _.pick(
            {
                ...dataExtraction.getDefaultsForItemType( item.schema, item.itemType.name ),
                ...dataExtraction.extractPropertiesOfItem( item )
            },
            backendActionProps
        );

        let retVal : BackendAction | undefined;

        if ( props ) {
            for ( const referenceId of item.references ) {
                const reference = global.data[ dataContext ]?.references?.[ referenceId ];

                if (
                    reference
                    && !reference.isDeleted()
                    && reference.destItemId === item.id
                    && reference.srcItemId
                ) {
                    const srcItem = global.data[ dataContext ]?.items[ reference.srcItemId ];

                    if (
                        srcItem
                        && !srcItem.isDeleted()
                    ) {
                        if ( srcItem.itemType.name === 'Group' ) {
                            if ( !props.groups ) {
                                props.groups = [];
                            }
                            props.groups.push( srcItem.key );
                        }
                        else if ( srcItem.itemType.name === 'User' ) {
                            props.user = srcItem.key;
                        }
                        else if (
                            srcItem
                            && !srcItem.isDeleted()
                            && srcItem.itemType.name === 'Config'
                        ) {
                            const configProperties = this.extractPropertiesOfItem( srcItem ),
                                  referenceData    = dataExtraction.extractPropertiesOfReference( reference ),
                                  label            = ( referenceData.label ?? String( srcItem?.key ) ) as string;
                            if ( configProperties ) {
                                props.configs = props.configs ?? {};
                                props.configs[ label ] = {
                                    ...configProperties
                                };
                            }
                        }
                    }
                }
            }
        }

        if ( requiredBackendActionProps.every( key => props[ key ] !== undefined ) ) {
            retVal = props;
        }

        return retVal;
    },

    extractBusinessProcessData( item : Item, dataContext : string ) : BusinessProcess | undefined {
        const data                                                   = _.pick(
                  {
                      ...dataExtraction.getDefaultsForItemType( item.schema, item.itemType.name ),
                      ...dataExtraction.extractPropertiesOfItem( item )
                  },
                  businessProcessDataProps
              ),
              dataStructure                                          = dataExtraction.extractDataStructure( item, dataContext, undefined ),
              steps                                                  = dataExtraction.extractBusinessProcessSteps( item, dataContext, dataStructure ),
              flatSteps : BusinessProcessStep[]                      = [],
              stepToIndexMapping : { [ stepKey : string ] : number } = {};

        for ( let i = 0; i < steps.length; i++ ) {
            const step = ( Array.isArray( steps[ i ] ) ? steps[ i ] : [ steps[ i ] ] ) as BusinessProcessStep[];

            for ( const stepItem of step ) {
                flatSteps.push( stepItem );
                stepToIndexMapping[ stepItem.data.key ] = i;
            }
        }

        // order step connections
        for ( const step of flatSteps ) {
            const stepIndex = stepToIndexMapping[ step.data.key ];

            // order out connections by target
            //  - backlinks desc,
            //  - (forward-)links asc
            step.connections?.out?.sort( ( connection1, connection2 ) => {
                const target1 = flatSteps.find( s => s.data.key === connection1.step.key ),
                      target2 = flatSteps.find( s => s.data.key === connection2.step.key );

                if ( target1 && target2 ) {
                    const index1 = stepToIndexMapping[ target1.data.key ],
                          index2 = stepToIndexMapping[ target2.data.key ];

                    // both connections are backlinks
                    if ( index1 < stepIndex && index2 < stepIndex ) {
                        if ( index1 > index2 ) {
                            return -1;
                        }
                        else if ( index1 < index2 ) {
                            return 1;
                        }
                        else if ( target1.data.subOrder > target2.data.subOrder ) {
                            return 1;
                        }
                        else if ( target1.data.subOrder < target2.data.subOrder ) {
                            return -1;
                        }
                    }
                    // only connection1 is a backlink
                    else if ( index1 < stepIndex ) {
                        return -1;
                    }
                    // only connection2 is a backlink
                    else if ( index2 < stepIndex ) {
                        return 1;
                    }
                    // no connection is a backlink
                    else {
                        if ( index1 > index2 ) {
                            return 1;
                        }
                        else if ( index1 < index2 ) {
                            return -1;
                        }
                        else if ( target1.data.subOrder > target2.data.subOrder ) {
                            return 1;
                        }
                        else if ( target1.data.subOrder < target2.data.subOrder ) {
                            return -1;
                        }
                    }
                }

                return 0;
            } );

            // order in connections by source
            //  - backlinks asc
            //  - (forward-)links desc
            step.connections?.in?.sort( ( connection1, connection2 ) => {
                const target1 = flatSteps.find( s => s.data.key === connection1.step.key ),
                      target2 = flatSteps.find( s => s.data.key === connection2.step.key );

                if ( target1 && target2 ) {
                    const index1 = stepToIndexMapping[ target1.data.key ],
                          index2 = stepToIndexMapping[ target2.data.key ];

                    // both connections are backlinks
                    if ( index1 >= stepIndex && index2 >= stepIndex ) {
                        if ( index1 > index2 ) {
                            return 1;
                        }
                        else if ( index1 < index2 ) {
                            return -1;
                        }
                        else if ( target1.data.subOrder > target2.data.subOrder ) {
                            return 1;
                        }
                        else if ( target1.data.subOrder < target2.data.subOrder ) {
                            return -1;
                        }
                    }
                    // connection1 is a backlink
                    else if ( index1 >= stepIndex ) {
                        return -1;
                    }
                    // connection2 is a backlink
                    else if ( index2 >= stepIndex ) {
                        return 1;
                    }
                    // both connections are forward links
                    if ( index1 < stepIndex - 1 && index2 < stepIndex - 1 ) {
                        if ( index1 > index2 ) {
                            return -1;
                        }
                        else if ( index1 < index2 ) {
                            return 1;
                        }
                        else if ( target1.data.subOrder > target2.data.subOrder ) {
                            return 1;
                        }
                        else if ( target1.data.subOrder < target2.data.subOrder ) {
                            return -1;
                        }
                    }
                    // no connection is a backlink or a forward one
                    else {
                        if ( index1 > index2 ) {
                            return -1;
                        }
                        else if ( index1 < index2 ) {
                            return 1;
                        }
                        else if ( target1.data.subOrder > target2.data.subOrder ) {
                            return 1;
                        }
                        else if ( target1.data.subOrder < target2.data.subOrder ) {
                            return -1;
                        }
                    }
                }

                return 0;
            } );
        }

        return {
            data,
            dataStructure,
            steps: steps.length ? steps : undefined
        };
    },

    extractBusinessProcessStepData( item : Item, dataContext : string, dataStructure? : DataStructure ) : BusinessProcessStep | undefined {
        const data                                             = _.pick(
                  {
                      ...dataExtraction.getDefaultsForItemType( item.schema, item.itemType.name ),
                      ...dataExtraction.extractPropertiesOfItem( item )
                  },
                  businessProcessStepDataProps
              ),
              messages                                         = dataExtraction.extractMessages( item, dataContext ),
              condition                                        = dataExtraction.extractCondition( item, dataContext, dataStructure ),
              connections : BusinessProcessStep['connections'] = {
                  in : [],
                  out: []
              };

        for ( const referenceId of item.references ) {
            const reference = global.data[ dataContext ]?.references?.[ referenceId ];

            if (
                reference
                && !reference.isDeleted()
                && reference.srcItemId
                && reference.destItemId
                && reference.referenceSet.referenceType.name === 'isConnectedTo'
            ) {
                const refData = {
                    ...dataExtraction.getDefaultsForReferenceSet( reference.schema, reference.referenceSetId ),
                    ...dataExtraction.extractPropertiesOfReference( reference )
                };

                if ( reference.srcItemId === item.id ) {
                    const destItem = global.data[ dataContext ]?.items[ reference.destItemId ];

                    if (
                        destItem
                        && !destItem.isDeleted()
                        && destItem.itemType.name === 'BusinessProcessStep'
                    ) {
                        connections.out.push( {
                            id   : reference.id,
                            step : {
                                id  : destItem.id,
                                key : destItem.key,
                                name: destItem.name
                            },
                            color: refData.color,
                            label: refData.label
                        } );
                    }
                }
                if ( reference.destItemId === item.id ) {
                    const srcItem = global.data[ dataContext ]?.items[ reference.srcItemId ];

                    if (
                        srcItem
                        && !srcItem.isDeleted()
                        && srcItem.itemType.name === 'BusinessProcessStep'
                    ) {
                        connections.in.push( {
                            id   : reference.id,
                            step : {
                                id  : srcItem.id,
                                key : srcItem.key,
                                name: srcItem.name
                            },
                            color: refData.color,
                            label: refData.label
                        } );
                    }
                }
            }
        }

        return {
            data,
            messages,
            condition,
            connections
        };
    },

    /**
     * Extracts data for column from given item
     *
     * @param {Item} item
     * @param {string} dataContext
     * @param {DataStructure} dataStructure
     *
     * @return {Column}
     */
    extractColumnData( item : Item, dataContext : string, dataStructure : DataStructure ) : Column {
        const data           = _.pick(
                  {
                      ...dataExtraction.getDefaultsForItemType( item.schema, item.itemType.name ),
                      ...dataExtraction.extractPropertiesOfItem( item )
                  },
                  columnDataProps
              ),
              dataObject     = extractDataObjectForItem( item, dataContext, dataStructure ),
              dataDescriptor = dataObject ? buildDataDescriptor( data, dataObject ) : undefined;

        if ( data.filterConfig ) {
            try {
                if ( data.filterConfig !== 'false' ) {
                    data.filterConfig = JSON.parse( data.filterConfig );

                    if ( data.filterConfig.schema ) {
                        data.filterConfig.schema = data.filterConfig.schema?.toUpperCase() as Schema;
                    }

                    if ( !data.filterConfig.valueSetId && data.filterConfig.valueSetName ) {
                        data.filterConfig.valueSetId = metaModel[ ( data.filterConfig.schema ?? 'DATA' ) as Schema ].getValueSet( data.filterConfig.valueSetName )?.id;
                    }

                    data.filterConfig = _.pick( data.filterConfig, filterConfig ) as FilterConfig;
                }
                else {
                    data.filterConfig = false;
                }
            }
            catch ( e ) {
                // do nothing
            }
        }

        if ( data.editorConfig ) {
            try {
                data.editorConfig = JSON.parse( data.editorConfig );

                if ( data.editorConfig.schema ) {
                    data.editorConfig.schema = data.editorConfig.schema?.toUpperCase() as Schema;
                }

                if ( !data.editorConfig.valueSetId && data.editorConfig.valueSetName ) {
                    data.editorConfig.valueSetId = metaModel[ ( data.editorConfig.schema ?? 'DATA' ) as Schema ].getValueSet( data.editorConfig.valueSetName )?.id;
                }

                data.editorConfig = _.pick( data.editorConfig, editorConfig ) as EditorConfig;
            }
            catch ( e ) {
                // do nothing
            }
        }

        const retVal : Column = {
            data,
            dataDescriptor,
            format        : dataExtraction.extractFormat( item, dataContext ),
            messages      : dataExtraction.extractMessages( item, dataContext ),
            panelConfigIds: dataExtraction.extractPanelConfigIds( item, dataContext ),
            permissions   : dataExtraction.extractPermissions( item, dataContext ),
            styles        : dataExtraction.extractStyles( item, dataContext, dataStructure, undefined, dataDescriptor )
        };

        return retVal;
    },

    /**
     * Extracts data for calendar from given item
     *
     * @param {Item} item
     * @param {string} dataContext
     * @param {DataStructure} dataStructure
     *
     * @return {Calendar}
     */
    extractCalendarData( item : Item, dataContext : string ) : Calendar {
        return {
            data       : _.pick(
                {
                    ...dataExtraction.getDefaultsForItemType( item.schema, item.itemType.name ),
                    ...dataExtraction.extractPropertiesOfItem( item )
                },
                calendarDataProps
            ),
            query      : dataExtraction.extractQuery( item, dataContext )!,
            images     : dataExtraction.extractImages( item, dataContext ),
            messages   : dataExtraction.extractMessages( item, dataContext ),
            permissions: dataExtraction.extractPermissions( item, dataContext )
        };
    },

    /**
     * Extracts data for calendarEvent from given item
     *
     * @param {Item} item
     * @param {string} dataContext
     * @param {DataStructure} dataStructure
     * @param {Query} [query]
     *
     * @return {Promise<CalendarEvent | undefined>}
     */
    async extractCalendarEventData( item : Item, dataContext : string, dataStructure : DataStructure, query? : Query ) : Promise<CalendarEvent | undefined> {
        const data                               = _.pick(
                  {
                      ...dataExtraction.getDefaultsForItemType( item.schema, item.itemType.name ),
                      ...dataExtraction.extractPropertiesOfItem( item )
                  },
                  calendarEventDataProps
              ),
              messages                           = dataExtraction.extractMessages( item, dataContext, undefined, dataStructure ),
              styles                             = dataExtraction.extractStyles( item, dataContext, dataStructure ),
              dataDescriptors : DataDescriptor[] = [];

        let columnDataDescriptors : StaticDataDescriptor[] | undefined,
            dataObject : DataObjectProperties | undefined,
            rootDataDescriptor : ObjectDataDescriptor | undefined,
            fromDataDescriptor : DataDescriptor | undefined,
            toDataDescriptor : DataDescriptor | undefined,
            allDayDataDescriptor : DataDescriptor | undefined,
            backgroundDataDescriptor : DataDescriptor | undefined;

        if ( ( query?.type === 'Cypher' || query?.type === 'SQL' ) && query?.query ) {

///////////////////////////////////////
/////////////////////////////////////////////////////////////////
////////////////////////////////////////////////
/////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////
/////////////////////////////////////////////////
////////////////////////////
//////////////////////////////////////////
///////////////////////
///////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////

//////////////////////////////////

////////////////////////////////////////////
/////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////
/////////////////////////////
///////////////////////////////
/////////////////
/////////////
//////////////////
//////////////////////////////////
/////////////

////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////

//////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////
//////////////////////
        }
        else {
            dataObject = extractDataObjectForItem( item, dataContext, dataStructure, 'providesDataFor' );
            rootDataDescriptor = dataObject
                ? getObjectDataDescriptor(
                    {
                        attributePath: 'item.id'
                    } as DataDescriptorBuildParams,
                    dataObject
                )
                : undefined;

            fromDataDescriptor = dataObject
                ? buildDataDescriptor(
                    {
                        aggregateFunction: data.fromAggregateFunction,
                        attributePath    : data.fromAttributePath
                    } as DataDescriptorBuildParams,
                    dataObject
                )
                : undefined;

            toDataDescriptor = dataObject
                ? buildDataDescriptor(
                    {
                        aggregateFunction: data.toAggregateFunction,
                        attributePath    : data.toAttributePath
                    } as DataDescriptorBuildParams,
                    dataObject
                )
                : undefined;

            allDayDataDescriptor = dataObject
                ? buildDataDescriptor(
                    {
                        attributePath: data.allDayAttributePath
                    } as DataDescriptorBuildParams,
                    dataObject
                )
                : undefined;

            backgroundDataDescriptor = dataObject
                ? buildDataDescriptor(
                    {
                        attributePath: data.backgroundAttributePath
                    } as DataDescriptorBuildParams,
                    dataObject
                )
                : undefined;
        }

        if ( fromDataDescriptor && toDataDescriptor ) {
            dataDescriptors.push( fromDataDescriptor );
            dataDescriptors.push( toDataDescriptor );

            if ( rootDataDescriptor ) {
                dataDescriptors.push( rootDataDescriptor );
            }

            if ( allDayDataDescriptor ) {
                dataDescriptors.push( allDayDataDescriptor );
            }

            if ( backgroundDataDescriptor ) {
                dataDescriptors.push( backgroundDataDescriptor );
            }

            // get dataDescriptors for messages
            if ( messages && Object.keys( messages ).length ) {
                const processedAttributePaths : string[] = [];

                for ( const message of Object.values( messages ) ) {

                    // get dataDescriptor(s) for non-string message labeled dataObjects
                    if ( typeof message !== 'string' ) {
                        const content        = message.content
                                  ? Object.values( message.content ).join( ' ' )
                                  : __( message.key ),
                              attributePaths = content.match( /((?:[\w\p{L}\p{N}:]+\.)+[\w\p{L}\p{N}:]+)/gimu ) ?? [ content ];

                        for ( let attributePath of attributePaths ) {
                            attributePath = attributePath.replaceAll( /(^\{|\}$)/g, '' );

                            if ( !processedAttributePaths.includes( attributePath ) ) {
                                processedAttributePaths.push( attributePath );

                                const attributePathParts = attributePath.split( '.' ),
                                      dataObjectLabel    = attributePathParts.shift();

                                let dataDescriptor : DataDescriptor | undefined;

                                if ( dataObjectLabel === 'item' || dataObjectLabel === 'property' ) {
                                    dataDescriptor = buildDataDescriptor(
                                        { attributePath: attributePath } as DataDescriptorBuildParams,
                                        dataStructure
                                    );
                                }
                                else if ( dataObjectLabel && message.dataObjects[ dataObjectLabel ] ) {
                                    dataDescriptor = buildDataDescriptor(
                                        { attributePath: attributePathParts.join( '.' ) } as DataDescriptorBuildParams,
                                        message.dataObjects[ dataObjectLabel ]
                                    );
                                }

                                if ( dataDescriptor ) {
                                    dataDescriptors.push( dataDescriptor );
                                }
                            }
                        }
                    }

                    // get dataDescriptor(s) for string message via columnDataDescriptors
                    else if ( typeof message === 'string' ) {
                        const attributePaths = __( message ).match( /((?:[\w\p{L}\p{N}:]+\.)+[\w\p{L}\p{N}:]+)/gimu ) ?? [ __( message ) ];

                        for ( let attributePath of attributePaths ) {
                            attributePath = attributePath.replaceAll( /(^\{|\}$)/g, '' );

                            let dataDescriptor : DataDescriptor | undefined;

                            if ( attributePath.match( /^(item|property)\./ ) ) {
                                dataDescriptor = buildDataDescriptor(
                                    { attributePath: attributePath } as DataDescriptorBuildParams,
                                    dataStructure
                                );
                            }
                            else if ( columnDataDescriptors ) {
                                dataDescriptor = columnDataDescriptors?.find( cDD => cDD.field === attributePath );
                            }

                            if ( dataDescriptor && !dataDescriptors.map( dD => dD.alias ).includes( dataDescriptor.alias ) ) {
                                dataDescriptors.push( dataDescriptor );
                            }
                        }
                    }
                }
            }

            // get dataDescriptors of style conditions
            if ( styles && Object.keys( styles ).length ) {
                const getConditionDataDescriptorRecursive = ( styleCondition : Condition ) => {

                    // get leftExpression dataDescriptor
                    if ( styleCondition.leftExpressionDataDescriptor?.alias ) {
                        if ( !dataDescriptors.find( dD => dD.alias === styleCondition.leftExpressionDataDescriptor!.alias ) ) {
                            dataDescriptors.push( styleCondition.leftExpressionDataDescriptor );
                        }
                    }

                    // get dataDescriptor for string leftExpression via columnDataDescriptors
                    else if ( typeof styleCondition.leftExpression === 'string' && columnDataDescriptors ) {
                        const attributePath  = styleCondition.leftExpression,
                              dataDescriptor = columnDataDescriptors?.find( cDD => cDD.field === attributePath );

                        if ( dataDescriptor && !dataDescriptors.find( dD => dD.alias === dataDescriptor.alias ) ) {
                            dataDescriptors.push( dataDescriptor );
                        }
                    }

                    if ( Array.isArray( styleCondition.leftExpression ) ) {
                        for ( const subCondition of styleCondition.leftExpression ) {
                            getConditionDataDescriptorRecursive( subCondition );
                        }
                    }

                    // get rightExpression dataDescriptor
                    if ( styleCondition.rightExpressionDataDescriptor?.alias ) {
                        if ( !dataDescriptors.find( dD => dD.alias === styleCondition.rightExpressionDataDescriptor!.alias ) ) {
                            dataDescriptors.push( styleCondition.rightExpressionDataDescriptor );
                        }
                    }

                    // get dataDescriptor for string rightExpression via columnDataDescriptors
                    else if ( typeof styleCondition.rightExpression === 'string' && columnDataDescriptors ) {
                        const attributePath  = styleCondition.rightExpression,
                              dataDescriptor = columnDataDescriptors?.find( cDD => cDD.field === attributePath );

                        if ( dataDescriptor && !dataDescriptors.find( dD => dD.alias === dataDescriptor.alias ) ) {
                            dataDescriptors.push( dataDescriptor );
                        }
                    }

                    if ( Array.isArray( styleCondition.rightExpression ) ) {
                        for ( const subCondition of styleCondition.rightExpression ) {
                            getConditionDataDescriptorRecursive( subCondition );
                        }
                    }
                };

                for ( const style of Object.keys( styles ) ) {
                    for ( const subStyle of styles[ style ] ?? [] ) {
                        if ( subStyle.condition ) {
                            getConditionDataDescriptorRecursive( subStyle.condition );
                        }
                    }
                }
            }

            return {
                data,
                rootDataDescriptor,
                fromDataDescriptor,
                toDataDescriptor,
                allDayDataDescriptor,
                backgroundDataDescriptor,
                dataDescriptors,
                messages,
                panelConfigIds: dataExtraction.extractPanelConfigIds( item, dataContext ),
                permissions   : dataExtraction.extractPermissions( item, dataContext ),
                styles
            };
        }
        else {
            return undefined;
        }
    },

    /**
     * Extracts data for chart from given item
     *
     * @param {Item} item
     * @param {string} dataContext
     *
     * @return {Chart}
     */
    extractChartData( item : Item, dataContext : string ) : Chart | undefined {
        const data                     = _.pick(
                  {
                      ...dataExtraction.getDefaultsForItemType( item.schema, item.itemType.name ),
                      ...dataExtraction.extractPropertiesOfItem( item )
                  },
                  chartDataProps
              ),
              query                    = dataExtraction.extractQuery( item, dataContext )!,
              images                   = dataExtraction.extractImages( item, dataContext ),
              messages                 = dataExtraction.extractMessages( item, dataContext ),
              permissions              = dataExtraction.extractPermissions( item, dataContext ),
              xAxisLabelDataObject     = query?.dataStructure
                  ? extractDataObjectForItem( item, dataContext, query.dataStructure, 'providesDataFor' )
                  : undefined,
              xAxisLabelDataDescriptor = xAxisLabelDataObject
                  ? buildDataDescriptor(
                      {
                          attributePath: data.xAxisLabelAttributePath
                      } as DataDescriptorBuildParams,
                      xAxisLabelDataObject
                  )
                  : buildDataDescriptor(
                      {
                          queryFieldName: data.xAxisLabelQueryFieldName
                      },
                      getDataStructureForSqlQuery()
                  );

        return {
            data,
            xAxisLabelDataDescriptor,
            query,
            images,
            messages,
            permissions
        };
    },

    /**
     * Extracts data for chartDataSet from given item
     *
     * @param {Item} item
     * @param {string} dataContext
     * @param {DataStructure} dataStructure
     *
     * @return {ChartDataSet | undefined}
     */
    extractChartDataSetData( item : Item, dataContext : string, dataStructure : DataStructure ) : ChartDataSet | undefined {
        const data                               = _.pick(
                  {
                      ...dataExtraction.getDefaultsForItemType( item.schema, item.itemType.name ),
                      ...dataExtraction.extractPropertiesOfItem( item )
                  },
                  chartDataSetDataProps
              ),
              messages                           = dataExtraction.extractMessages( item, dataContext, undefined, dataStructure ),
              permissions                        = dataExtraction.extractPermissions( item, dataContext ),
              styles                             = dataExtraction.extractStyles( item, dataContext, dataStructure ),
              dataObject                         = extractDataObjectForItem( item, dataContext, dataStructure, 'providesDataFor' ),
              dataDescriptor                     = dataObject
                  ? buildDataDescriptor(
                      {
                          aggregateFunction: data.aggregateFunction,
                          attributePath    : data.attributePath
                      },
                      dataObject
                  )
                  : buildDataDescriptor(
                      {
                          queryFieldName: data.queryFieldName
                      },
                      getDataStructureForSqlQuery()
                  ),
              dataDescriptors : DataDescriptor[] = [];

        let scale : string | undefined;

        if ( dataDescriptor ) {
            dataDescriptors.push( dataDescriptor );

            // get dataDescriptors for messages
            if ( dataObject && messages && Object.keys( messages ).length ) {
                const processedAttributePaths : string[] = [];

                for ( const message of Object.values( messages ) ) {

                    // get dataDescriptor(s) for non-string message labeled dataObjects
                    if ( typeof message !== 'string' ) {
                        const content        = message.content
                                  ? Object.values( message.content ).join( ' ' )
                                  : __( message.key ),
                              attributePaths = content.match( /((?:[\w\p{L}\p{N}:]+\.)+[\w\p{L}\p{N}:]+)/gimu ) ?? [ content ];

                        for ( let attributePath of attributePaths ) {
                            attributePath = attributePath.replaceAll( /(^\{|\}$)/g, '' );

                            if ( !processedAttributePaths.includes( attributePath ) ) {
                                processedAttributePaths.push( attributePath );

                                const attributePathParts = attributePath.split( '.' ),
                                      dataObjectLabel    = attributePathParts.shift();

                                let messageDataDescriptor : DataDescriptor | undefined;

                                if ( dataObjectLabel === 'item' || dataObjectLabel === 'property' ) {
                                    messageDataDescriptor = buildDataDescriptor(
                                        { attributePath: attributePath } as DataDescriptorBuildParams,
                                        dataStructure
                                    );
                                }
                                else if ( dataObjectLabel && message.dataObjects[ dataObjectLabel ] ) {
                                    messageDataDescriptor = buildDataDescriptor(
                                        { attributePath: attributePathParts.join( '.' ) } as DataDescriptorBuildParams,
                                        message.dataObjects[ dataObjectLabel ]
                                    );
                                }

                                if ( messageDataDescriptor ) {
                                    dataDescriptors.push( messageDataDescriptor );
                                }
                            }
                        }
                    }

                    // get dataDescriptor(s) for string message via columnDataDescriptors
                    else if ( typeof message === 'string' ) {
                        const attributePaths = __( message ).match( /((?:[\w\p{L}\p{N}:]+\.)+[\w\p{L}\p{N}:]+)/gimu ) ?? [ __( message ) ];

                        for ( let attributePath of attributePaths ) {
                            attributePath = attributePath.replaceAll( /(^\{|\}$)/g, '' );

                            let messageDataDescriptor : DataDescriptor | undefined;

                            if ( attributePath.match( /^(item|property|reference|referenceProperty)\./ ) ) {
                                messageDataDescriptor = buildDataDescriptor(
                                    {
                                        attributePath: attributePath
                                    },
                                    dataStructure
                                );
                            }

                            if ( messageDataDescriptor && !dataDescriptors.map( dD => dD.alias ).includes( messageDataDescriptor.alias ) ) {
                                dataDescriptors.push( messageDataDescriptor );
                            }
                        }
                    }
                }
            }

            // get dataDescriptors of style conditions
            if ( dataObject && styles && Object.keys( styles ).length ) {
                const getConditionDataDescriptorRecursive = ( condition : Condition ) => {

                    // get leftExpression dataDescriptor
                    if ( condition.leftExpressionDataDescriptor?.alias ) {
                        if ( !dataDescriptors.find( dD => dD.alias === condition.leftExpressionDataDescriptor!.alias ) ) {
                            dataDescriptors.push( condition.leftExpressionDataDescriptor );
                        }
                    }

                    if ( Array.isArray( condition.leftExpression ) ) {
                        for ( const subCondition of condition.leftExpression ) {
                            getConditionDataDescriptorRecursive( subCondition );
                        }
                    }

                    // get rightExpression dataDescriptor
                    if ( condition.rightExpressionDataDescriptor?.alias ) {
                        if ( !dataDescriptors.find( dD => dD.alias === condition.rightExpressionDataDescriptor!.alias ) ) {
                            dataDescriptors.push( condition.rightExpressionDataDescriptor );
                        }
                    }

                    if ( Array.isArray( condition.rightExpression ) ) {
                        for ( const subCondition of condition.rightExpression ) {
                            getConditionDataDescriptorRecursive( subCondition );
                        }
                    }
                };

                for ( const style of Object.keys( styles ) ) {
                    for ( const subStyle of styles[ style ] ?? [] ) {
                        if ( subStyle.condition ) {
                            getConditionDataDescriptorRecursive( subStyle.condition );
                        }
                    }
                }
            }
        }

        // get scale
        for ( const referenceId of item.references ) {
            const reference = global.data[ dataContext ]?.references?.[ referenceId ];

            if (
                reference
                && !reference.isDeleted()
                && reference.srcItemId === item.id
                && reference.destItemId
                && reference.referenceSet.referenceType.name === 'belongsTo'
            ) {
                const destItem = global.data[ dataContext ]?.items[ reference.destItemId ];

                if (
                    destItem
                    && !destItem.isDeleted()
                    && destItem.itemType.name === 'ChartScale'
                ) {
                    scale = destItem.key;
                }
            }
        }

        return {
            data,
            scale,
            dataDescriptor,
            dataDescriptors,
            messages,
            permissions,
            styles
        };
    },

    /**
     * Extracts data for chart scale from given item
     *
     * @param {Item} item
     * @param {string} dataContext
     *
     * @return {ChartScale | undefined}
     */
    extractChartScaleData( item : Item, dataContext : string ) : ChartScale | undefined {
        return {
            data    : _.pick(
                {
                    ...dataExtraction.getDefaultsForItemType( item.schema, item.itemType.name ),
                    ...dataExtraction.extractPropertiesOfItem( item )
                },
                chartScaleDataProps
            ),
            messages: dataExtraction.extractMessages( item, dataContext )
        };
    },

    /**
     * Extracts data for condition from given item
     *
     * @param {Item} item
     * @param {string} dataContext
     * @param {DataStructure} dataStructure
     * @param {DataDescriptor} [defaultDataDescriptor]
     * @param {boolean} [simpleDescriptor=false] set to true for simple alias like Key, Name, <PropertyName> (used by tree)
     * @param {boolean} [ignoreErrors=false]
     *
     * @return {Condition}
     */
    extractConditionData( item : Item, dataContext : string, dataStructure? : DataStructure, defaultDataDescriptor? : DataDescriptor, simpleDescriptor : boolean = false, ignoreErrors : boolean = false ) : Condition {
        const retVal = {
            ...dataExtraction.getDefaultsForItemType( item.schema, item.itemType.name ),
            ...dataExtraction.extractPropertiesOfItem( item )
        };

        // AND or OR operator
        // -> resolve leftExpression as array
        //    by Condition - isLeftExpressionFor -> Condition
        if ( retVal.operator === 'and' || retVal.operator === 'or' ) {
            retVal.leftExpression = [] as Condition[];

            for ( const referenceId of item.references ) {
                const reference = global.data[ dataContext ]?.references?.[ referenceId ];

                if (
                    reference
                    && !reference.isDeleted()
                    && reference.destItemId === item.id
                    && reference.srcItemId
                    && reference.referenceSet.referenceType.name === 'isLeftExpressionFor'
                ) {
                    const srcItem = global.data[ dataContext ]?.items[ reference.srcItemId ];

                    if (
                        srcItem
                        && !srcItem.isDeleted()
                        && srcItem.itemType.name === 'Condition'
                    ) {
                        retVal.leftExpression.push( dataExtraction.extractConditionData( srcItem, dataContext, dataStructure, defaultDataDescriptor, simpleDescriptor, ignoreErrors ) );
                    }
                }
            }

            if ( !retVal.leftExpression.length ) {
                delete retVal.leftExpression;
            }
        }
        // -> resolve leftExpressionDataDescriptor if
        else if ( typeof retVal.leftExpression === 'string' ) {
            for ( const referenceId of item.references ) {
                const reference = global.data[ dataContext ]?.references?.[ referenceId ];

                if (
                    dataStructure
                    && reference
                    && !reference.isDeleted()
                    && reference.destItemId === item.id
                    && reference.srcItemId
                    && reference.referenceSet.referenceType.name === 'providesDataForLeftExpression'
                ) {
                    const srcItem     = global.data[ dataContext ]?.items[ reference.srcItemId ],
                          dataObjects = extractDataObjectsFromDataStructure( dataStructure ),
                          dataObject  = getDataObjectByOrigId( reference.srcItemId, dataStructure );

                    if (
                        srcItem
                        && !srcItem.isDeleted()
                        && srcItem.itemType.name === 'DataObject'
                        && dataObject
                    ) {
                        dataObjects.unshift( dataObject )
                    }

                    retVal.leftExpressionDataDescriptor = buildDataDescriptor( { attributePath: retVal.leftExpression }, dataObjects, simpleDescriptor );

                    delete retVal.leftExpression;
                    break;
                }
            }

            // if no DataObjects were referenced use dataObject of defaultDataDescriptor
            if ( retVal.leftExpression && defaultDataDescriptor ) {
                const dataObjects = defaultDataDescriptor.type !== 'expression'
                    ? [ defaultDataDescriptor.dataObject ]
                    : defaultDataDescriptor.dataDescriptors.reduce(
                        ( innerRetVal, dD ) => {
                            if ( !innerRetVal.find( iRV => iRV.origId === dD.dataObject.origId ) ) {
                                innerRetVal.push( dD.dataObject );
                            }

                            return innerRetVal;
                        },
                        [] as DataStructure[] );

                retVal.leftExpressionDataDescriptor = buildDataDescriptor( { attributePath: retVal.leftExpression }, dataObjects, simpleDescriptor );
                delete retVal.leftExpression;
            }
        }

        if ( retVal.leftExpression === undefined && !retVal.leftExpressionDataDescriptor ) {
            if ( defaultDataDescriptor ) {
                retVal.leftExpressionDataDescriptor = defaultDataDescriptor;
            }
            else if ( !ignoreErrors ) {
                throw ( new LocalizableError(
                    'YN.base.backend.dataExtraction.extractConditionData.noLeftExpression',
                    {
                        key: retVal.key
                    }
                ) );
            }
        }


        // AND, OR, EMPTY or NOTEMPTY operator
        // -> no rightExpression
        if ( retVal.operator === 'and' || retVal.operator === 'or' || retVal.operator === 'empty' || retVal.operator === 'notEmpty' ) {
            delete retVal.rightExpression;
        }

            // IN or NOTIN operator
            // -> resolve rightExpression as array
        //    by Condition - isRightExpressionFor -> Condition
        else if ( retVal.operator === 'in' || retVal.operator === 'notIn' ) {
            retVal.rightExpression = [] as Condition[];

            for ( const referenceId of item.references ) {
                const reference = global.data[ dataContext ]?.references?.[ referenceId ];

                if (
                    reference
                    && !reference.isDeleted()
                    && reference.destItemId === item.id
                    && reference.srcItemId
                    && reference.referenceSet.referenceType.name === 'isRightExpressionFor'
                ) {
                    const srcItem = global.data[ dataContext ]?.items[ reference.srcItemId ];

                    if (
                        srcItem
                        && !srcItem.isDeleted()
                        && srcItem.itemType.name === 'Condition'
                    ) {
                        retVal.rightExpression.push( dataExtraction.extractConditionData( srcItem, dataContext, dataStructure, defaultDataDescriptor, simpleDescriptor, ignoreErrors ) );
                    }
                }
            }

            if ( !retVal.rightExpression.length ) {
                delete retVal.rightExpression;
            }
        }

        // -> resolve rightExpressionDataDescriptor if
        else if ( typeof retVal.rightExpression === 'string' ) {
            for ( const referenceId of item.references ) {
                const reference = global.data[ dataContext ]?.references?.[ referenceId ];

                if (
                    dataStructure
                    && reference
                    && !reference.isDeleted()
                    && reference.destItemId === item.id
                    && reference.srcItemId
                    && reference.referenceSet.referenceType.name === 'providesDataForRightExpression'
                ) {
                    const srcItem     = global.data[ dataContext ]?.items[ reference.srcItemId ],
                          dataObjects = extractDataObjectsFromDataStructure( dataStructure ),
                          dataObject  = getDataObjectByOrigId( reference.srcItemId, dataStructure );

                    if (
                        srcItem
                        && !srcItem.isDeleted()
                        && srcItem.itemType.name === 'DataObject'
                        && dataObject
                    ) {
                        dataObjects.unshift( dataObject )
                    }

                    retVal.rightExpressionDataDescriptor = buildDataDescriptor( { attributePath: retVal.leftExpression }, dataObjects, simpleDescriptor );

                    delete retVal.rightExpression;
                    break;
                }
            }
        }

        if ( retVal.rightExpression === undefined && !retVal.rightExpressionDataDescriptor && retVal.operator !== 'and' && retVal.operator !== 'or' && retVal.operator !== 'empty' && retVal.operator !== 'notEmpty' && retVal.operator !== undefined ) {
            if ( defaultDataDescriptor ) {
                retVal.rightExpressionDataDescriptor = defaultDataDescriptor;
            }
            else if ( !ignoreErrors ) {
                throw ( new LocalizableError(
                    'YN.base.backend.dataExtraction.extractConditionData.noRightExpression',
                    {
                        key: retVal.key
                    }
                ) );
            }
        }

        return _.pick( retVal, conditionProps ) as Condition;
    },

    extractDataChangeEventData( item : Item, dataContext : string ) {
        const retVal = {
            data        : _.pick(
                {
                    ...dataExtraction.getDefaultsForItemType( item.schema, item.itemType.name ),
                    ...dataExtraction.extractPropertiesOfItem( item ),
                    backendActions: dataExtraction.extractBackendActions( item, dataContext )?.map( bA => bA.key ),
                    condition     : dataExtraction.extractCondition( item, dataContext )
                },
                dataChangeEventData
            ) as unknown as DataChangeEventData,
            itemType    : undefined as ( { schema : Schema; id : number; } | undefined ),
            referenceSet: undefined as ( { schema : Schema; id : number; } | undefined )
        };

        for ( const referenceId of item.references ) {
            const reference = global.data[ dataContext ]?.references?.[ referenceId ];

            if (
                reference
                && !reference.isDeleted()
                && reference.destItemId === item.id
                && reference.srcItemId
                && reference.referenceSet.referenceType.name === 'isTriggering'
            ) {
                const srcItem = global.data[ dataContext ]?.items[ reference.srcItemId ];

                if (
                    srcItem
                    && !srcItem.isDeleted()
                    && (
                        srcItem.itemType.name === 'YnItemType'
                        || srcItem.itemType.name === 'YnReferenceSet'
                    )
                ) {
                    const srcItemData = {
                        ...dataExtraction.getDefaultsForItemType( srcItem.schema, srcItem.itemType.name ),
                        ...dataExtraction.extractPropertiesOfItem( srcItem )
                    };

                    if ( srcItemData.schema ) {
                        const itemSchema = srcItemData.schema.toUpperCase() as Schema;

                        if ( srcItem.itemType.name === 'YnItemType' ) {
                            retVal.itemType = {
                                schema: itemSchema,
                                id    : metaModel[ itemSchema ].getItemType( srcItemData.name )!.id
                            };
                        }
                        else {
                            const parts = srcItemData.name.split( ':' );

                            retVal.referenceSet = {
                                schema: itemSchema,
                                id    : metaModel[ itemSchema ].getReferenceSetsForItemTypesAndReferenceType( parts[ 0 ], parts[ 2 ], parts[ 1 ], 'src' )![ 0 ].id
                            };
                        }
                    }
                }
            }
        }

        return retVal;
    },

    /**
     * Extracts data for dataObject from given item
     * @param {Item} item
     * @param {string} dataContext
     * @param {( ds : DataStructure, parentDs : DataStructure | undefined ) => void} [callback]
     * @param {DataStructure} [parentDataStructure]
     * @param {ExtractedReferenceProperties} [referenceToParent]
     * @param {{ [ id : number ] : number }} [idCount={}]
     * @param {boolean} [withDeleted=false] return deleted items too
     *
     * @return {DataStructure}
     */
    extractDataStructureData( item : Item, dataContext : string, callback ? : ( dataStructure : DataStructure, parentDataStructure : DataStructure | undefined ) => void, parentDataStructure ? : DataStructure, referenceToParent ? : ExtractedReferenceProperties, idCount : {
        [ id : number ] : number
    } = {}, withDeleted : boolean = false ) : DataStructure {
        let id = `do_${item.id}`;

        if ( idCount[ item.id ] ) {
            id = `${id}_${idCount[ item.id ]++}`;
        }
        else {
            idCount[ item.id ] = 1;
        }

        const retVal : DataStructure = {
            id,
            origId   : item.id,
            item     : {
                ...dataExtraction.getDefaultsForItemType( item.schema, item.itemType.name ),
                ...dataExtraction.extractPropertiesOfItem( item )
            },
            reference: referenceToParent
        };

        for ( const referenceId of item.references ) {
            const reference = global.data[ dataContext ]?.references?.[ referenceId ];

            let srcExtractedDataStructure : DataStructure;

            // look down (only handle references where current item is the destination), don't follow cyclic references
            if (
                reference
                && ( withDeleted || !reference.isDeleted() )
                && reference.destItemId === item.id
                && reference.srcItemId
                && reference.srcItemId !== parentDataStructure?.item.id
            ) {
                const srcItem            = global.data[ dataContext ]?.items[ reference.srcItemId ],
                      extractedReference = dataExtraction.extractPropertiesOfReference( reference );

                if (
                    srcItem
                    && ( withDeleted || !srcItem.isDeleted() )
                    && srcItem.itemType.name === 'DataObject'
                ) {
                    srcExtractedDataStructure = dataExtraction.extractDataStructureData( srcItem, dataContext, callback, retVal, extractedReference, idCount, withDeleted );

                    if ( !retVal.dataStructure ) {
                        retVal.dataStructure = [];
                    }
                    retVal.dataStructure.push( srcExtractedDataStructure );
                }
            }
        }

        callback?.( retVal, parentDataStructure );

        return retVal;
    },

    /**
     * Extracts data for mailAttachment from given item
     *
     * @param {Item} item
     *
     * @return {File}
     */
    extractFileData( item : Item ) : File {
        const data = {
            ...dataExtraction.getDefaultsForItemType( item.schema, item.itemType.name ),
            ...dataExtraction.extractPropertiesOfItem( item )
        };

        return _.pick( data, fileProps ) as File;
    },

    /**
     * Extracts data for function from given item
     *
     * @param {Item} item
     * @param {string} dataContext
     * @param {DataStructure} [dataStructure]
     *
     * @return {Function}
     */
    extractFunctionData( item : Item, dataContext : string, dataStructure ? : DataStructure ) : Function {
        const data = {
            ...dataExtraction.getDefaultsForItemType( item.schema, item.itemType.name ),
            ...dataExtraction.extractPropertiesOfItem( item ),
            dataObjects : {} as Function[ 'dataObjects' ],
            configs     : {} as Function[ 'configs' ],
            formElements: {} as Function[ 'formElements' ]
        };

        if ( dataStructure ) {
            for ( const referenceId of item.references ) {
                const reference = global.data[ dataContext ]?.references?.[ referenceId ];

                if (
                    reference
                    && !reference.isDeleted()
                ) {
                    if (
                        reference.destItemId === item.id
                        && reference.srcItemId
                        && reference.referenceSet.referenceType.name === 'isUsedBy'
                    ) {
                        const srcItem                              = global.data[ dataContext ]?.items[ reference.srcItemId ],
                              referenceData                        = dataExtraction.extractPropertiesOfReference( reference ),
                              dataObjectIsUsedByActionReferenceSet = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( 'DataObject', 'Function', 'isUsedBy', 'src' )?.[ 0 ],
                              dataObjectProperties                 = getDataObjectByOrigId( reference.srcItemId, dataStructure ),
                              configProperties                     = this.extractPropertiesOfItem( srcItem ),
                              label                                = ( referenceData.label ?? String( srcItem?.key ) ) as string,
                              dataSelectionMode                    = ( referenceData.dataSelectionMode ?? metaModel[ 'CFG' ].getReferencePropertySetForReferenceSetAndPropertyType( dataObjectIsUsedByActionReferenceSet.id, 'DataSelectionMode' )?.valueDefault ?? 'single' ) as DataSelectionMode;

                        if (
                            srcItem
                            && !srcItem.isDeleted()
                            && srcItem?.itemType.name === 'DataObject'
                            && dataObjectProperties
                        ) {
                            data.dataObjects[ label ] = {
                                ...dataObjectProperties,
                                dataSelectionMode
                            };
                        }
                        else if (
                            srcItem
                            && !srcItem.isDeleted()
                            && srcItem.itemType.name === 'Config'
                            && configProperties
                        ) {
                            data.configs![ label ] = {
                                ...configProperties
                            };
                        }
                    }
                    else if (
                        reference.srcItemId === item.id
                        && reference.destItemId
                        && reference.referenceSet.referenceType.name === 'isManipulatorFor'
                    ) {
                        const destItem      = global.data[ dataContext ]?.items[ reference.destItemId ],
                              referenceData = dataExtraction.extractPropertiesOfReference( reference ),
                              label         = ( referenceData.label ?? String( destItem.key ) ) as string;

                        if (
                            destItem
                            && !destItem.isDeleted()
                        ) {
                            data.formElements[ label ] = reference.destItemId;
                        }
                    }
                }
            }
        }

        return _.pick( data, functionProps ) as Function;
    },

    /**
     * Extracts data for calendar from given item
     *
     * @param {Item} item
     * @param {string} dataContext
     * @param {DataStructure} dataStructure
     *
     * @return {Calendar}
     */
    extractGraphData( item : Item, dataContext : string, ignoreConditionErrors : boolean = false ) : Graph {
        const query = dataExtraction.extractQuery( item, dataContext );

        return {
            data       : _.pick(
                {
                    ...dataExtraction.getDefaultsForItemType( item.schema, item.itemType.name ),
                    ...dataExtraction.extractPropertiesOfItem( item )
                },
                graphDataProps
            ),
            query,
            condition  : dataExtraction.extractCondition( item, dataContext, query?.dataStructure, undefined, ignoreConditionErrors ),
            images     : dataExtraction.extractImages( item, dataContext ),
            messages   : dataExtraction.extractMessages( item, dataContext, undefined, query?.dataStructure ),
            permissions: dataExtraction.extractPermissions( item, dataContext ),
            styles     : query?.dataStructure ? dataExtraction.extractStyles( item, dataContext, query.dataStructure ) : undefined
        };
    },

    /**
     * Extracts data for landingPage from given item
     *
     * @param {Item} item
     *
     * @return {PanelConfig}
     */
    extractLandingPageData( item : Item ) : LandingPageData | undefined {
        const props = _.pick(
            {
                ...dataExtraction.getDefaultsForItemType( item.schema, item.itemType.name ),
                ...dataExtraction.extractPropertiesOfItem( item )
            },
            landingPageDataProps
        );

        let retVal : LandingPageData | undefined;

        if ( requiredLandingPageDataProps.every( key => props[ key ] !== undefined ) ) {
            retVal = props;
        }

        return retVal;
    },

    /**
     * Extracts data for mailAttachment from given item
     *
     * @param {Item} item
     * @param {string} dataContext
     * @param {DataStructure} [dataStructure]
     *
     * @return {MailAttachment}
     */
    extractMailAttachmentData( item : Item, dataContext : string, dataStructure ? : DataStructure ) : MailAttachment {
        const data : MailAttachment = {
            ...dataExtraction.getDefaultsForItemType( item.schema, item.itemType.name ),
            ...dataExtraction.extractPropertiesOfItem( item ),
            messages: dataExtraction.extractMessages( item, dataContext, mailAttachmentMessages, dataStructure )
        };

        if ( dataStructure ) {
            for ( const referenceId of item.references ) {
                const reference = global.data[ dataContext ]?.references?.[ referenceId ];

                if ( reference ) {
                    if (
                        reference.destItemId === item.id
                        && reference.srcItemId
                        && reference.referenceSet.referenceType.name === 'providesDataFor'
                    ) {
                        const srcItem = global.data[ dataContext ]?.items[ reference.srcItemId ];

                        if (
                            srcItem
                            && !srcItem.isDeleted()
                            && srcItem.itemType.name === 'DataObject'
                        ) {
                            data.dataObject = getDataObjectByOrigId( reference.srcItemId, dataStructure );
                            break;
                        }
                    }
                }
            }
        }

        return _.pick( data, mailAttachmentProps ) as MailAttachment;
    },

    /**
     * Extracts data for mailServer from given item
     *
     * @param {Item} item
     *
     * @return {MailServer}
     */
    extractMailServerData( item : Item ) : MailServer {
        return _.pick(
            {
                ...dataExtraction.getDefaultsForItemType( item.schema, item.itemType.name ),
                ...dataExtraction.extractPropertiesOfItem( item )
            },
            mailServerProps
        );
    },

    /**
     * Extracts data for map from given item
     *
     * @param {Item} item
     * @param {string} dataContext
     * @param {DataStructure} dataStructure
     *
     * @return {Map}
     */
    extractMapData( item : Item, dataContext : string ) : Map {
        return {
            data       : _.pick(
                {
                    ...dataExtraction.getDefaultsForItemType( item.schema, item.itemType.name ),
                    ...dataExtraction.extractPropertiesOfItem( item )
                },
                mapDataProps
            ),
            images     : dataExtraction.extractImages( item, dataContext ),
            messages   : dataExtraction.extractMessages( item, dataContext ),
            permissions: dataExtraction.extractPermissions( item, dataContext )
        };
    },

    /**
     * Extracts data for mapDataSet from given item
     *
     * @param {Item} item
     * @param {string} dataContext
     *
     * @return {MapDataSet | undefined}
     */
    extractMapDataSetDataDataStructureQueryOnly( item : Item, dataContext : string ) : MapDataSet | undefined {
        const data                               = _.pick(
                  {
                      ...dataExtraction.getDefaultsForItemType( item.schema, item.itemType.name ),
                      ...dataExtraction.extractPropertiesOfItem( item )
                  },
                  mapDataSetDataProps
              ),
              query                              = dataExtraction.extractQuery( item, dataContext ),
              condition                          = query?.dataStructure ? dataExtraction.extractCondition( item, dataContext, query.dataStructure, undefined, true ) : undefined,
              styles                             = query?.dataStructure ? dataExtraction.extractStyles( item, dataContext, query.dataStructure ) : undefined,
              images                             = dataExtraction.extractImages( item, dataContext ),
              messages                           = dataExtraction.extractMessages( item, dataContext, undefined, query?.dataStructure ),
              permissions                        = dataExtraction.extractPermissions( item, dataContext ),
              dataDescriptors : DataDescriptor[] = [];

        let dataObject : DataObjectProperties | undefined,
            rootDataDescriptor : ObjectDataDescriptor | undefined,
            connectToDataDescriptor : DataDescriptor | undefined,
            locationDataDescriptor : DataDescriptor | undefined,
            timeDataDescriptor : DataDescriptor | undefined;

        if ( query?.dataStructure ) {
            dataObject = extractDataObjectForItem( item, dataContext, query.dataStructure, 'isRootFor' );
            rootDataDescriptor = dataObject
                ? getObjectDataDescriptor(
                    {
                        attributePath: 'item.id'
                    } as DataDescriptorBuildParams,
                    dataObject
                )
                : undefined;

            dataObject = extractDataObjectForItem( item, dataContext, query.dataStructure, 'providesConnectToDataFor' );
            connectToDataDescriptor = dataObject
                ? buildDataDescriptor(
                    {
                        aggregateFunction: data.connectToAggregateFunction,
                        attributePath    : data.connectToAttributePath
                    } as DataDescriptorBuildParams,
                    dataObject
                )
                : undefined;

            dataObject = extractDataObjectForItem( item, dataContext, query.dataStructure, 'providesDataFor' );
            locationDataDescriptor = dataObject
                ? buildDataDescriptor(
                    {
                        aggregateFunction: data.locationAggregateFunction,
                        attributePath    : data.locationAttributePath
                    } as DataDescriptorBuildParams,
                    dataObject
                )
                : undefined;

            dataObject = extractDataObjectForItem( item, dataContext, query.dataStructure, 'providesTimeDataFor' );
            timeDataDescriptor = dataObject
                ? buildDataDescriptor(
                    {
                        aggregateFunction: data.timeAggregateFunction,
                        attributePath    : data.timeAttributePath
                    } as DataDescriptorBuildParams,
                    dataObject
                )
                : undefined;

            if ( rootDataDescriptor ) {
                dataDescriptors.push( rootDataDescriptor );
            }

            if ( connectToDataDescriptor ) {
                dataDescriptors.push( connectToDataDescriptor );
            }

            if ( locationDataDescriptor ) {
                dataDescriptors.push( locationDataDescriptor );
            }

            if ( timeDataDescriptor ) {
                dataDescriptors.push( timeDataDescriptor );
            }

            // get dataDescriptors for messages
            if ( messages && Object.keys( messages ).length ) {
                const processedAttributePaths : string[] = [];

                for ( const message of Object.values( messages ) ) {

                    // get dataDescriptor(s) for non-string message via labeled dataObjects
                    if ( typeof message !== 'string' ) {
                        const content        = message.content
                                  ? Object.values( message.content ).join( ' ' )
                                  : __( message.key ),
                              attributePaths = content.match( /((?:[\w\p{L}\p{N}:]+\.)+[\w\p{L}\p{N}:]+)/gimu ) ?? [ content ];

                        for ( let attributePath of attributePaths ) {
                            attributePath = attributePath.replaceAll( /(^\{|\}$)/g, '' );

                            if ( !processedAttributePaths.includes( attributePath ) ) {
                                processedAttributePaths.push( attributePath );

                                const attributePathParts = attributePath.split( '.' ),
                                      dataObjectLabel    = attributePathParts.shift();

                                let dataDescriptor : DataDescriptor | undefined;

                                if ( query.dataStructure && ( dataObjectLabel === 'item' || dataObjectLabel === 'property' ) ) {
                                    dataDescriptor = buildDataDescriptor(
                                        { attributePath: attributePath } as DataDescriptorBuildParams,
                                        query.dataStructure
                                    );
                                }
                                else if ( dataObjectLabel && message.dataObjects[ dataObjectLabel ] ) {
                                    dataDescriptor = buildDataDescriptor(
                                        { attributePath: attributePathParts.join( '.' ) } as DataDescriptorBuildParams,
                                        message.dataObjects[ dataObjectLabel ]
                                    );
                                }

                                if ( dataDescriptor ) {
                                    dataDescriptors.push( dataDescriptor );
                                }
                            }
                        }
                    }

                    // get dataDescriptor(s) for string message via key-labelwd dataObjects
                    else {
                        const attributePaths = __( message ).match( /((?:[\w\p{L}\p{N}:]+\.)+[\w\p{L}\p{N}:]+)/gimu ) ?? [ __( message ) ];

                        for ( let attributePath of attributePaths ) {
                            attributePath = attributePath.replaceAll( /(^\{|\}$)/g, '' );

                            let dataDescriptor : DataDescriptor | undefined;

                            if ( attributePath.match( /^(item|property)\./ ) ) {
                                dataDescriptor = buildDataDescriptor(
                                    { attributePath: attributePath } as DataDescriptorBuildParams,
                                    query.dataStructure
                                );
                            }

                            if ( dataDescriptor && !dataDescriptors.map( dD => dD.alias ).includes( dataDescriptor.alias ) ) {
                                dataDescriptors.push( dataDescriptor );
                            }
                        }
                    }
                }
            }

            // get dataDescriptors of style conditions
            if ( styles && Object.keys( styles ).length ) {
                const getConditionDataDescriptorRecursive = ( styleCondition : Condition ) => {

                    // get leftExpression dataDescriptor
                    if ( styleCondition.leftExpressionDataDescriptor?.alias ) {
                        if ( !dataDescriptors.find( dD => dD.alias === styleCondition.leftExpressionDataDescriptor!.alias ) ) {
                            dataDescriptors.push( styleCondition.leftExpressionDataDescriptor );
                        }
                    }

                    if ( Array.isArray( styleCondition.leftExpression ) ) {
                        for ( const subCondition of styleCondition.leftExpression ) {
                            getConditionDataDescriptorRecursive( subCondition );
                        }
                    }

                    // get rightExpression dataDescriptor
                    if ( styleCondition.rightExpressionDataDescriptor?.alias ) {
                        if ( !dataDescriptors.find( dD => dD.alias === styleCondition.rightExpressionDataDescriptor!.alias ) ) {
                            dataDescriptors.push( styleCondition.rightExpressionDataDescriptor );
                        }
                    }

                    if ( Array.isArray( styleCondition.rightExpression ) ) {
                        for ( const subCondition of styleCondition.rightExpression ) {
                            getConditionDataDescriptorRecursive( subCondition );
                        }
                    }
                };

                for ( const style of Object.keys( styles ) ) {
                    for ( const subStyle of styles[ style ] ?? [] ) {
                        if ( subStyle.condition ) {
                            getConditionDataDescriptorRecursive( subStyle.condition );
                        }
                    }
                }
            }

            return {
                data,
                query,
                condition,
                rootDataDescriptor,
                connectToDataDescriptor,
                locationDataDescriptor,
                timeDataDescriptor,
                dataDescriptors,
                images,
                messages,
                permissions,
                styles
            };
        }

        return undefined;
    },

    /**
     * Extracts data for mapDataSet from given item
     *
     * @param {Item} item
     * @param {string} dataContext
     *
     * @return {Promise<MapDataSet | undefined>}
     */
    async extractMapDataSetData( item : Item, dataContext : string ) : Promise<MapDataSet | undefined> {
        const data                               = _.pick(
                  {
                      ...dataExtraction.getDefaultsForItemType( item.schema, item.itemType.name ),
                      ...dataExtraction.extractPropertiesOfItem( item )
                  },
                  mapDataSetDataProps
              ),
              query                              = dataExtraction.extractQuery( item, dataContext ),
              condition                          = query?.dataStructure ? dataExtraction.extractCondition( item, dataContext, query.dataStructure ) : undefined,
              styles                             = query?.dataStructure ? dataExtraction.extractStyles( item, dataContext, query.dataStructure ) : undefined,
              images                             = dataExtraction.extractImages( item, dataContext ),
              messages                           = dataExtraction.extractMessages( item, dataContext, undefined, query?.dataStructure ),
              permissions                        = dataExtraction.extractPermissions( item, dataContext ),
              dataDescriptors : DataDescriptor[] = [];

        let columnDataDescriptors : StaticDataDescriptor[] | undefined,
            dataObject : DataObjectProperties | undefined,
            rootDataDescriptor : DataDescriptor | undefined,
            connectToDataDescriptor : DataDescriptor | undefined,
            locationDataDescriptor : DataDescriptor | undefined,
            timeDataDescriptor : DataDescriptor | undefined;

        if ( ( query?.type === 'Cypher' || query?.type === 'SQL' ) && query?.query ) {

            // TODO: implement logic for frontend use

///////////////////////////////////////
/////////////////////////////////////////////////////////////////
////////////////////////////////////////////////
/////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////
/////////////////////////////////////////////////
////////////////////////////
//////////////////////////////////////////
///////////////////////
///////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////

//////////////////////////////////

////////////////////////////////////////////
/////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////
/////////////////////////////
///////////////////////////////
/////////////////
/////////////
//////////////////
//////////////////////////////////
/////////////

////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////

//////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////
//////////////////////
        }
        else if ( query?.dataStructure ) {
            dataObject = extractDataObjectForItem( item, dataContext, query.dataStructure, 'isRootFor' );
            rootDataDescriptor = dataObject
                ? getObjectDataDescriptor(
                    {
                        attributePath: 'item.id'
                    } as DataDescriptorBuildParams,
                    dataObject
                )
                : undefined;

            dataObject = extractDataObjectForItem( item, dataContext, query.dataStructure, 'providesConnectToDataFor' );
            connectToDataDescriptor = dataObject
                ? buildDataDescriptor(
                    {
                        aggregateFunction: data.connectToAggregateFunction,
                        attributePath    : data.connectToAttributePath
                    } as DataDescriptorBuildParams,
                    dataObject
                )
                : undefined;

            dataObject = extractDataObjectForItem( item, dataContext, query.dataStructure, 'providesDataFor' );
            locationDataDescriptor = dataObject
                ? buildDataDescriptor(
                    {
                        aggregateFunction: data.locationAggregateFunction,
                        attributePath    : data.locationAttributePath
                    } as DataDescriptorBuildParams,
                    dataObject
                )
                : undefined;

            dataObject = extractDataObjectForItem( item, dataContext, query.dataStructure, 'providesTimeDataFor' );
            timeDataDescriptor = dataObject
                ? buildDataDescriptor(
                    {
                        aggregateFunction: data.timeAggregateFunction,
                        attributePath    : data.timeAttributePath
                    } as DataDescriptorBuildParams,
                    dataObject
                )
                : undefined;
        }

        if ( query && locationDataDescriptor ) {
            dataDescriptors.push( locationDataDescriptor );

            if ( rootDataDescriptor ) {
                dataDescriptors.push( rootDataDescriptor );
            }

            if ( connectToDataDescriptor ) {
                dataDescriptors.push( connectToDataDescriptor );
            }

            if ( timeDataDescriptor ) {
                dataDescriptors.push( timeDataDescriptor );
            }

            // get dataDescriptors for messages
            if ( messages && Object.keys( messages ).length ) {
                const processedAttributePaths : string[] = [];

                for ( const message of Object.values( messages ) ) {

                    // get dataDescriptor(s) for non-string message labeled dataObjects
                    if ( typeof message !== 'string' ) {
                        const content        = message.content
                                  ? Object.values( message.content ).join( ' ' )
                                  : __( message.key ),
                              attributePaths = content.match( /((?:[\w\p{L}\p{N}:]+\.)+[\w\p{L}\p{N}:]+)/gimu ) ?? [ content ];

                        for ( let attributePath of attributePaths ) {
                            attributePath = attributePath.replaceAll( /(^\{|\}$)/g, '' );

                            if ( !processedAttributePaths.includes( attributePath ) ) {
                                processedAttributePaths.push( attributePath );

                                const attributePathParts = attributePath.split( '.' ),
                                      dataObjectLabel    = attributePathParts.shift();

                                let dataDescriptor : DataDescriptor | undefined;

                                if ( query.dataStructure && ( dataObjectLabel === 'item' || dataObjectLabel === 'property' ) ) {
                                    dataDescriptor = buildDataDescriptor(
                                        { attributePath: attributePath } as DataDescriptorBuildParams,
                                        query.dataStructure
                                    );
                                }
                                else if ( dataObjectLabel && message.dataObjects[ dataObjectLabel ] ) {
                                    dataDescriptor = buildDataDescriptor(
                                        { attributePath: attributePathParts.join( '.' ) } as DataDescriptorBuildParams,
                                        message.dataObjects[ dataObjectLabel ]
                                    );
                                }

                                if ( dataDescriptor ) {
                                    dataDescriptors.push( dataDescriptor );
                                }
                            }
                        }
                    }

                    // get dataDescriptor(s) for string message via columnDataDescriptors
                    else {
                        const attributePaths = __( message ).match( /((?:[\w\p{L}\p{N}:]+\.)+[\w\p{L}\p{N}:]+)/gimu ) ?? [ __( message ) ];

                        for ( let attributePath of attributePaths ) {
                            attributePath = attributePath.replaceAll( /(^\{|\}$)/g, '' );

                            let dataDescriptor : DataDescriptor | undefined;

                            if ( query.dataStructure && attributePath.match( /^(item|property)\./ ) ) {
                                dataDescriptor = buildDataDescriptor(
                                    { attributePath: attributePath } as DataDescriptorBuildParams,
                                    query.dataStructure
                                );
                            }
                            else if ( columnDataDescriptors ) {
                                dataDescriptor = columnDataDescriptors?.find( cDD => cDD.field === attributePath );
                            }

                            if ( dataDescriptor && !dataDescriptors.map( dD => dD.alias ).includes( dataDescriptor.alias ) ) {
                                dataDescriptors.push( dataDescriptor );
                            }
                        }
                    }
                }
            }

            // get dataDescriptors of style conditions
            if ( styles && Object.keys( styles ).length ) {
                const getConditionDataDescriptorRecursive = ( conditionToProcess : Condition ) => {

                    // get leftExpression dataDescriptor
                    if ( conditionToProcess.leftExpressionDataDescriptor?.alias ) {
                        if ( !dataDescriptors.find( dD => dD.alias === conditionToProcess.leftExpressionDataDescriptor!.alias ) ) {
                            dataDescriptors.push( conditionToProcess.leftExpressionDataDescriptor );
                        }
                    }

                    // get dataDescriptor for string leftExpression via columnDataDescriptors
                    else if ( typeof conditionToProcess.leftExpression === 'string' && columnDataDescriptors ) {
                        const attributePath  = conditionToProcess.leftExpression,
                              dataDescriptor = columnDataDescriptors?.find( cDD => cDD.field === attributePath );

                        if ( dataDescriptor && !dataDescriptors.find( dD => dD.alias === dataDescriptor.alias ) ) {
                            dataDescriptors.push( dataDescriptor );
                        }
                    }

                    if ( Array.isArray( conditionToProcess.leftExpression ) ) {
                        for ( const subCondition of conditionToProcess.leftExpression ) {
                            getConditionDataDescriptorRecursive( subCondition );
                        }
                    }

                    // get rightExpression dataDescriptor
                    if ( conditionToProcess.rightExpressionDataDescriptor?.alias ) {
                        if ( !dataDescriptors.find( dD => dD.alias === conditionToProcess.rightExpressionDataDescriptor!.alias ) ) {
                            dataDescriptors.push( conditionToProcess.rightExpressionDataDescriptor );
                        }
                    }

                    // get dataDescriptor for string rightExpression via columnDataDescriptors
                    else if ( typeof conditionToProcess.rightExpression === 'string' && columnDataDescriptors ) {
                        const attributePath  = conditionToProcess.rightExpression,
                              dataDescriptor = columnDataDescriptors?.find( cDD => cDD.field === attributePath );

                        if ( dataDescriptor && !dataDescriptors.find( dD => dD.alias === dataDescriptor.alias ) ) {
                            dataDescriptors.push( dataDescriptor );
                        }
                    }

                    if ( Array.isArray( conditionToProcess.rightExpression ) ) {
                        for ( const subCondition of conditionToProcess.rightExpression ) {
                            getConditionDataDescriptorRecursive( subCondition );
                        }
                    }
                };

                for ( const style of Object.keys( styles ) ) {
                    for ( const subStyle of styles[ style ] ?? [] ) {
                        if ( subStyle.condition ) {
                            getConditionDataDescriptorRecursive( subStyle.condition );
                        }
                    }
                }
            }

            return {
                data,
                query,
                condition,
                rootDataDescriptor,
                connectToDataDescriptor,
                locationDataDescriptor,
                timeDataDescriptor,
                dataDescriptors,
                images,
                messages,
                permissions,
                styles
            };
        }
        else {
            return undefined;
        }
    },

    /**
     * Extracts data for message from given item
     *
     * @param {Item} item
     * @param {string} dataContext
     * @param {DataStructure} [dataStructure]
     *
     * @return {Message}
     */
    extractMessageData( item : Item, dataContext : string, dataStructure ? : DataStructure ) : Message {
        const dataObjects : MessageObject[ 'dataObjects' ] = {};

        let retVal : Message = item.key;

        if ( dataStructure ) {
            for ( const referenceId of item.references ) {
                const reference = global.data[ dataContext ]?.references?.[ referenceId ];

                if (
                    reference
                    && !reference.isDeleted()
                    && reference.destItemId === item.id
                    && reference.srcItemId
                    && reference.referenceSet.referenceType.name === 'isUsedBy'
                ) {
                    const srcItem                              = global.data[ dataContext ]?.items[ reference.srcItemId ],
                          referenceData                        = dataExtraction.extractPropertiesOfReference( reference ),
                          dataObjectIsUsedByActionReferenceSet = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( 'DataObject', 'Message', 'isUsedBy', 'src' )?.[ 0 ],
                          dataObjectProperties                 = getDataObjectByOrigId( reference.srcItemId, dataStructure ),
                          label                                = ( referenceData.label ?? String( srcItem?.key ) ) as string,
                          dataSelectionMode                    = ( referenceData.dataSelectionMode ?? metaModel[ 'CFG' ].getReferencePropertySetForReferenceSetAndPropertyType( dataObjectIsUsedByActionReferenceSet.id, 'DataSelectionMode' )?.valueDefault ?? 'single' ) as DataSelectionMode;

                    if (
                        srcItem
                        && !srcItem.isDeleted()
                        && srcItem.itemType.name === 'DataObject'
                        && dataObjectProperties
                    ) {
                        dataObjects[ label ] = {
                            ...dataObjectProperties,
                            dataSelectionMode
                        };
                    }
                }
            }
        }

        if ( Object.keys( dataObjects ).length ) {
            const data : MessageObject = {
                ...dataExtraction.getDefaultsForItemType( item.schema, item.itemType.name ),
                ...dataExtraction.extractPropertiesOfItem( item ),
                dataObjects
            };

            retVal = _.pick( data, messageObjectProps ) as MessageObject;
        }

        return retVal;
    },

    /**
     * Extracts data for panelConfig from given item
     *
     * @param {Item} item
     *
     * @return {PanelConfig}
     */
    extractPanelConfigData( item : Item ) : PanelConfig {
        const retVal : PanelConfig = _.pick(
            {
                ...dataExtraction.getDefaultsForItemType( item.schema, item.itemType.name ),
                ...dataExtraction.extractPropertiesOfItem( item )
            },
            panelConfigProps
        );

        for ( const referenceId of item.references ) {
            const reference = global.data[ item.dataContext ]?.references?.[ referenceId ];

            if (
                reference
                && !reference.isDeleted()
                && reference.destItemId === item.id
                && reference.srcItemId
                && reference.referenceSet.referenceType.name === 'isApplying'
            ) {
                const srcItem = global.data[ item.dataContext ]?.items[ reference.srcItemId ];

                if (
                    srcItem
                    && !srcItem.isDeleted()
                    && srcItem.itemType.name === 'Condition'
                ) {
                    try {
                        retVal.condition = dataExtraction.extractConditionData( srcItem, item.dataContext );
                    }
                    catch ( e ) {
                        const stackError = new StackError(
                            'YN.base.backend.dataExtraction.extractPanelConfigData.extractCondition.error',
                            {
                                key: retVal.key
                            }
                        );

                        if ( e instanceof Error || typeof e === 'string' ) {
                            stackError.add( e );
                        }

                        throw ( stackError );
                    }

                    break;
                }
            }
        }

        if ( retVal.schema ) {
            retVal.schema = retVal.schema.toUpperCase() as Schema;
        }

        if ( retVal.config ) {
            try {
                retVal.config = JSON.parse( String( retVal.config ) );
            }
            catch ( e ) {
                // silent
            }
        }

        return retVal
    },

    /**
     * Extracts data for an item from given item and structure
     *
     * @param {Item} item
     * @param {string} dataContext
     *
     * @return {Data}
     */
    extractOverviewData( item : Item, dataContext : string ) : Overview {
        let groupingColumn : string | undefined = undefined;

        for ( const referenceId of item.references ) {
            const reference = global.data[ dataContext ]?.references?.[ referenceId ];

            if (
                reference
                && !reference.isDeleted()
                && reference.destItemId === item.id
                && reference.srcItemId
                && reference.referenceSet.referenceType.name === 'isGrouping'
            ) {
                const srcItem = global.data[ dataContext ]?.items[ reference.srcItemId ];

                if (
                    srcItem
                    && !srcItem.isDeleted()
                    && srcItem.itemType.name === 'Column'
                ) {
                    groupingColumn = srcItem.key;
                }
            }
        }

        return {
            data          : _.pick(
                {
                    ...dataExtraction.getDefaultsForItemType( item.schema, item.itemType.name ),
                    ...dataExtraction.extractPropertiesOfItem( item )
                },
                overviewDataProps
            ) as Overview[ 'data' ],
            groupingColumn,
            query         : dataExtraction.extractQuery( item, dataContext )!,
            images        : dataExtraction.extractImages( item, dataContext ),
            actions       : dataExtraction.extractActions( item, dataContext ),
            functions     : dataExtraction.extractFunctions( item, dataContext ),
            messages      : dataExtraction.extractMessages( item, dataContext ),
            panelConfigIds: dataExtraction.extractPanelConfigIds( item, dataContext ),
            permissions   : dataExtraction.extractPermissions( item, dataContext ),
            style         : []
        };
    },

    /**
     * Extracts data for query from given item
     *
     * @param {Item} item
     * @param {string} dataContext
     *
     * @return {Query}
     */
    extractQueryData( item : Item, dataContext : string ) : Query {
        const dataStructure = dataExtraction.extractDataStructure( item, dataContext ),
              data          = {
                  ...dataExtraction.getDefaultsForItemType( item.schema, item.itemType.name ),
                  ...dataExtraction.extractPropertiesOfItem( item ),
                  dataStructure,
                  parameters: dataExtraction.extractQueryParameters( item, dataContext, dataStructure )
              },
              retVal        = _.pick( data, queryProps ) as Query;

        retVal.schema = retVal.schema.toUpperCase() as Schema;

        return retVal;
    },

    /**
     * Extracts the parameter markers used in the given Query.
     *
     * @param {Item} item
     * @param {string} dataContext
     * @param {DataStructure} [dataStructure]
     *
     * @return {string[]|undefined}
     */
    extractQueryParameters( item : Item, dataContext : string, dataStructure ? : DataStructure ) : string[ ] | undefined {
        let retVal : string[] | undefined | null;

        if ( item.itemType.name === 'Query' ) {
            const typePropertySet = metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( 'Query', 'Type' );

            if ( typePropertySet ) {
                const type = ( item.properties.get( typePropertySet.propertyTypeId )?.value ?? typePropertySet.valueDefault ) as string;

                if ( type === 'Cypher' || type === 'SQL' ) {
                    const queryPropertyTypeId = metaModel[ 'CFG' ].getPropertyType( 'Query' )?.id;

                    if ( queryPropertyTypeId && item.properties.has( queryPropertyTypeId ) ) {
                        retVal = ( item.properties.get( queryPropertyTypeId )?.value as string ?? '' ).match( /(?<=\$\{?)([\w\p{L}\p{N}]|\.)+(?=\}|[^\w\p{L}\p{N}]|$)/gimu );
                    }
                }

                // DataStructure
                else {
                    if ( !dataStructure ) {
                        dataStructure = dataExtraction.extractDataStructure( item, dataContext );
                    }

                    // TODO: implement if needed and implemented in DataStructure/Condition
                }
            }
        }

        if ( !retVal?.length || retVal === null ) {
            retVal = undefined;
        }
        else {
            retVal = _.uniq( retVal );
        }

        return retVal;
    },

    /**
     * Extracts data for style from given item
     *
     * @param {Item} item
     * @param {string} dataContext
     * @param {DataStructure} dataStructure
     * @param {DataDescriptor} [defaultDataDescriptor]
     *
     * @return {Style}
     */
    extractStyleData( item : Item, dataContext : string, dataStructure : DataStructure, defaultDataDescriptor ? : DataDescriptor ) : Style {
        const data = {
            ...dataExtraction.getDefaultsForItemType( item.schema, item.itemType.name ),
            ...dataExtraction.extractPropertiesOfItem( item )
        };

        // convert enum className to string
        if ( data.className && typeof data.className === 'object' && Object.keys( data.className ).length ) {
            const classNames : string[] = [];

            for ( const className of Object.keys( data.className ) as string[] ) {
                classNames.push( className )
            }

            data.className = classNames.join( ' ' );
        }

        for ( const referenceId of item.references ) {
            const reference = global.data[ dataContext ]?.references?.[ referenceId ];

            if (
                reference
                && !reference.isDeleted()
                && reference.destItemId === item.id
                && reference.srcItemId
                && reference.referenceSet.referenceType.name === 'isApplying'
            ) {
                const srcItem = global.data[ dataContext ]?.items[ reference.srcItemId ];

                if (
                    srcItem
                    && !srcItem.isDeleted()
                    && srcItem.itemType.name === 'Condition'
                ) {
                    try {
                        data.condition = dataExtraction.extractConditionData( srcItem, dataContext, dataStructure, defaultDataDescriptor );
                    }
                    catch ( e ) {
                        const stackError = new StackError(
                            'YN.base.backend.dataExtraction.extractStyleData.extractCondition.error',
                            {
                                key: data.key
                            }
                        );

                        if ( e instanceof Error || typeof e === 'string' ) {
                            stackError.add( e );
                        }

                        throw ( stackError );
                    }

                    break;
                }
            }
        }

        return _.pick( data, styleProps ) as Style;
    },

    /**
     * Extracts data for function from given item
     *
     * @param {Item} item
     * @param {string} dataContext
     * @param {DataStructure} [dataStructure]
     *
     * @return {Function}
     */
    extractValidatorData( item : Item, dataContext : string, dataStructure ? : DataStructure ) : Validator {
        const data = {
            ...dataExtraction.getDefaultsForItemType( item.schema, item.itemType.name ),
            ...dataExtraction.extractPropertiesOfItem( item ),
            dataObjects: {} as Validator[ 'dataObjects' ],
            message    : dataExtraction.extractMessages( item, dataContext, [ 'errorMessage' ] )?.[ 'errorMessage' ]
        };

        if ( dataStructure ) {
            for ( const referenceId of item.references ) {
                const reference = global.data[ dataContext ]?.references?.[ referenceId ];

                if (
                    reference
                    && !reference.isDeleted()
                    && reference.destItemId === item.id
                    && reference.srcItemId
                    && reference.referenceSet.referenceType.name === 'isUsedBy'
                ) {
                    const srcItem                              = global.data[ dataContext ]?.items[ reference.srcItemId ],
                          referenceData                        = dataExtraction.extractPropertiesOfReference( reference ),
                          dataObjectIsUsedByActionReferenceSet = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( 'DataObject', 'Validator', 'isUsedBy', 'src' )?.[ 0 ],
                          dataObjectProperties                 = getDataObjectByOrigId( reference.srcItemId, dataStructure ),
                          label                                = ( referenceData.label ?? String( srcItem?.key ) ) as string,
                          dataSelectionMode                    = ( referenceData.dataSelectionMode ?? metaModel[ 'CFG' ].getReferencePropertySetForReferenceSetAndPropertyType( dataObjectIsUsedByActionReferenceSet.id, 'DataSelectionMode' )?.valueDefault ?? 'single' ) as DataSelectionMode;

                    if (
                        srcItem
                        && !srcItem.isDeleted()
                        && srcItem.itemType.name === 'DataObject'
                        && dataObjectProperties
                    ) {
                        data.dataObjects[ label ] = {
                            ...dataObjectProperties,
                            dataSelectionMode
                        };
                    }
                }
            }
        }

        return _.pick( data, validatorProps ) as Validator;
    },

    /**
     * Extracts the key of a referenced item described by referenceDefinitions
     *
     * @param {Item} item
     * @param {string} dataContext
     * @param {{ direction : 'SrcToDest' | 'DestToSrc', referenceType : string, targetItemType : string }[]} referenceDefinitions
     *
     * @return {string | undefined}
     */
    extractReferencedItemKey( item : Item, dataContext : string, referenceDefinitions : {
        direction : 'SrcToDest' | 'DestToSrc',
        referenceType : string,
        targetItemType : string
    }[] ) : string | undefined {
        let retVal : string | undefined;

        for ( const referenceId of item.references ) {
            const reference = global.data[ dataContext ]?.references?.[ referenceId ];

            if (
                reference
                && !reference.isDeleted()
            ) {
                for ( const referenceDefinition of referenceDefinitions ) {
                    if (
                        reference.referenceSet.referenceType.name === referenceDefinition.referenceType
                        && (
                            (
                                referenceDefinition.direction === 'SrcToDest'
                                && reference.srcItemId === item.id
                                && reference.destItemId
                            )
                            ||
                            (
                                referenceDefinition.direction === 'DestToSrc'
                                && reference.destItemId === item.id
                                && reference.srcItemId
                            )
                        )
                    ) {
                        const refItem = global.data[ dataContext ]?.items[ referenceDefinition.direction === 'SrcToDest' ? reference.destItemId : reference.srcItemId ];

                        if (
                            refItem
                            && !refItem.isDeleted()
                            && refItem.itemType.name === referenceDefinition.targetItemType
                        ) {
                            retVal = refItem.key;

                            break;
                        }
                    }
                }
            }
        }

        return retVal;
    },

    /**
     * Extracts the attributes' id, key, and name and all properties and enumerated properties values to a flat object.
     *
     * @param {Item} item
     *
     * @return {ExtractedProperties}
     */
    extractPropertiesOfItem( item : Item ) : ExtractedProperties {
        const retVal : ExtractedProperties = {
            id  : item.id,
            key : item.key,
            name: item.name
        };

        for ( const property of item.properties.values() ) {
            if ( !property.isDeleted() ) {
                retVal[ _.lowerFirst( property.propertySet.name ) ] = property.value;
            }
        }

        for ( const enumProperty of item.enumProperties.values() ) {
            const propertyTypeName = _.lowerFirst( enumProperty.propertySet.name );

            retVal[ propertyTypeName ] = {};

            for ( const property of enumProperty.properties.values() ) {
                if ( !property.isDeleted() ) {
                    retVal[ propertyTypeName ][ property.stringKey ] = property.value;
                }
            }
        }

        return retVal;
    },

    /**
     * Extracts the attributes' id, key, and name and all properties and enumerated properties values to a flat object.
     *
     * @param {Reference} reference
     *
     * @return {ExtractedReferenceProperties}
     */
    extractPropertiesOfReference( reference : Reference ) : ExtractedReferenceProperties {
        const retVal : ExtractedReferenceProperties = {
            id        : reference.id,
            srcItemId : reference.srcItemId ?? reference.srcItem?.id ?? 0,
            destItemId: reference.destItemId ?? reference.destItem?.id ?? 0
        };

        for ( const property of reference.properties.values() ) {
            retVal[ _.lowerFirst( property.referencePropertySet.name ) ] = property.value;
        }

        for ( const enumProperty of reference.enumProperties.values() ) {
            const propertyTypeName = _.lowerFirst( enumProperty.referencePropertySet.name );

            retVal[ propertyTypeName ] = {};

            for ( const property of enumProperty.properties.values() ) {
                ( retVal[ propertyTypeName ] as {
                    [ key : string ] : AttributeValue
                } )[ property.stringKey ] = property.referencePropertySet.valueType === 'JSON'
                    ? JSON.parse( property.value as string )
                    : property.value;
            }
        }

        return retVal;
    },

    /**
     * Extracts the part of given ReferenceTypeName which is within is and For.
     *
     * @param {string} referenceTypeName
     *
     * @return {string}
     */
    extractIsForReferenceTypeName( referenceTypeName : string ) : string {
        const nameMatches = referenceTypeName.match( /^is(.*)For$/ );

        let retVal = referenceTypeName;

        if ( nameMatches ) {
            retVal = _.lowerFirst( nameMatches[ 1 ] );
        }

        return retVal;
    },

    /**
     * Gets the property defaults from MetaModel for ItemType
     *
     * @return {RequiredOf<FormFieldProperties>}
     *
     * @private
     */
    getDefaultsForItemType( schema : Schema, itemTypeName : string ) : { [ property : string ] : any } {
        const retVal : {
                  [ property : string ] : any
              }            = {},
              propertySets = metaModel[ schema ].getPropertySetsForItemType( itemTypeName );

        for ( const propertySet of propertySets ) {
            if ( propertySet?.valueDefault !== undefined && propertySet?.valueDefault !== '' ) {
                retVal[ _.lowerFirst( propertySet.name ) ] = propertySet.valueDefault;
            }
        }

        return retVal;
    },

    /**
     * Gets the property defaults from MetaModel for ReferenceSet
     *
     * @return {RequiredOf<FormFieldProperties>}
     *
     * @private
     */
    getDefaultsForReferenceSet( schema : Schema, referenceSetId : number ) : { [ property : string ] : any } {
        const retVal : {
                  [ property : string ] : any
              }            = {},
              propertySets = metaModel[ schema ].getReferencePropertySetsForReferenceSet( referenceSetId );

        for ( const propertySet of propertySets ) {
            if ( propertySet?.valueDefault !== undefined && propertySet?.valueDefault !== '' ) {
                retVal[ _.lowerFirst( propertySet.name ) ] = propertySet.valueDefault;
            }
        }

        return retVal;
    }
};
